import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { APIConstants, ClaimStatusConstants, CommonAPIConstants, PatientStatementsConstants } from "../utilities/Constants";
import { fetchWithAuth } from "../common/fetchWithAuth";
import "../css/PatientStatementsWorkflow.css";

const defaultIsoRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setMonth(to.getMonth() - 3);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

const statusTiles = [
  { key: "Pending Review", label: "Pending Review", color: "#f59e0b" },
  { key: "Approved", label: "Approved", color: "#16a34a" },
  { key: "Rejected", label: "Rejected", color: "#dc2626" },
  { key: "Generated", label: "Generated", color: "#2563eb" },
  { key: "All", label: "All", color: "#64748b" },
];

const pick = (source, keys, fallback = "-") => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const getRequestId = (request) => pick(request, ["StatementRequestId", "RequestID", "RequestId", "ID"], "");
const getStatus = (request) => pick(request, ["Status", "RequestStatus", "StatusText"], "");

const mergeDefined = (...sources) => {
  const target = {};
  sources.forEach((source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) target[key] = value;
    });
  });
  return target;
};

const billingDeskClaimColumns = [
  { label: "#", value: (_item, index) => index + 1, className: "psw-row-number" },
  { label: "Location", keys: ["Location", "location", "LocationNickname", "locationNickname", "LocationNickName", "PlaceOfService", "POS"] },
  { label: "DOS", keys: ["DOS", "dos", "ClaimDOS", "claimDOS", "ServiceDate", "serviceDate", "DateOfService"], type: "date" },
  { label: "Claim #", keys: ["ClaimID", "ClaimId", "claimID", "claimId", "claimid", "ClaimNumber", "claimNumber"] },
  { label: "Patient Name", keys: ["PatientName", "patientName", "Patient", "patient"] },
  { label: "BH/SUD", keys: ["BH_SUD", "BHSUD", "BH/SUD", "ServiceType", "CounselorAppType", "ScheduleTypeClass"] },
  { label: "DOB", keys: ["DOB", "dob", "DateOfBirth", "dateOfBirth"], type: "date" },
  { label: "Insurance(Pri)", keys: ["InsCompNickName", "insCompNickName", "InsurancePri", "PrimaryInsurance", "primaryInsurance", "InsurancePrimary", "sInsuranceName", "Payer", "payer"] },
  { label: "Insurance(Sec)", keys: ["SecInsCompNickName", "secInsCompNickName", "InsuranceSec", "SecondaryInsurance", "secondaryInsurance", "InsuranceSecondary", "SecPayer", "secPayer", "SecInsurance"] },
  { label: "ClearingHouse", keys: ["ClearingHouseName", "clearingHouseName", "ClearingHouse", "clearingHouse"] },
  { label: "Submit Date(Pri)", keys: ["SubmitDate", "submitDate", "ClaimSubmitDate", "claimSubmitDate"], type: "date" },
  { label: "Submit Date(Sec)", keys: ["SecClaimSubmitDate", "SecondarySubmitDate", "SecSubmitDate", "SecClaimSubmiDate"], type: "date" },
  { label: "CPT", keys: ["CPT", "cpt", "Procedure", "procedure", "CPTCode", "cptCode"] },
  { label: "Units", keys: ["Units", "units", "Unit", "unit"] },
  { label: "Status", keys: ["Status", "status", "ClaimStatus", "claimStatus", "intClaimStatus"] },
  { label: "Total Charge", keys: ["TotalCharge", "totalCharge", "Totalcharge", "totalcharge", "Charge", "charge"], type: "money" },
  { label: "Charge Received(Pri)", keys: ["RecvdCharge", "recvdCharge", "CPTRecvdCharge", "cptRecvdCharge", "ChargeReceivedPri", "PrimaryReceivedCharge"], type: "money" },
  { label: "Charge Received(Sec)", keys: ["SecrecvdCharge", "secRecvdCharge", "SecRecvdCharge", "ChargeReceivedSec", "SecondaryReceivedCharge"], type: "money" },
  { label: "Contractual Adjustment", keys: ["WriteOffAmount", "writeOffAmount", "ContractualAdjustment", "contractualAdjustment", "Adjustment", "adjustment"], type: "money" },
  { label: "Secondary Pending", keys: ["SecPendingAmt", "secPendingAmt", "SecondaryPending", "secondaryPending"], type: "money" },
  { label: "Deductible", keys: ["Deductible", "deductible"], type: "money" },
  { label: "Pat Res", keys: ["PatientAmount", "patientAmount", "PatResp", "patResp", "PatientBalance", "patientBalance", "PatientAmt", "patientAmt"], type: "money" },
  { label: "Insurance Balance", keys: ["InsuranceBalance", "insuranceBalance", "InsBalance", "insBalance"], type: "money" },
  { label: "Balance", keys: ["claimbln", "ClaimBalance", "claimBalance", "Balance", "balance", "ClaimBal", "balanceDue"], type: "money" },
  { label: "Payer Ref #", keys: ["PayerRefNumber", "payerRefNumber", "PayerclaimRefNumber", "ExternalClaimID", "externalClaimID"] },
  { label: "Claim Notes", keys: ["ClaimNotes", "claimNotes", "Notes", "notes"] },
];

const getPatientName = (patient) => {
  if (!patient) return "";
  const first = patient.FirstName || patient.firstName || "";
  const last = patient.LastName || patient.lastName || "";
  const full = [last, first].filter(Boolean).join(", ");
  return patient.PatientName || patient.Name || full || "";
};

const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/[$,]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatMoney = (value) => `$${parseAmount(value).toFixed(2)}`;

const getRowValue = (row, keys, fallback = "-") => {
  const fields = row?.Fields || row?.fields || row || {};
  return pick(fields, keys, fallback);
};

const sumRowsByKeys = (rows, keys) =>
  rows.reduce((total, row) => total + parseAmount(getRowValue(row, keys, 0)), 0);

const getBillingClaimTotalValue = (rows, column) => {
  if (column.label === "Location") return "Total";
  if (column.label === "Units") return sumRowsByKeys(rows, column.keys);
  if (column.type === "money") return formatMoney(sumRowsByKeys(rows, column.keys));
  return "";
};

const formatColumnValue = (value, type) => {
  if (value === undefined || value === null || value === "") return "-";
  if (type === "date") return formatDateOnly(value);
  if (type === "money") return formatMoney(value);
  return value;
};

const getClaimColumnValue = (item, column, index, selectedFields = {}, selectedPatientName = "") => {
  if (column.value) return column.value(item, index);
  const fallbackByLabel = {
    "Patient Name": selectedPatientName,
    Location: pick(selectedFields, ["Location", "LocationName"], ""),
    DOS: pick(selectedFields, ["DOS", "ServiceDate"], ""),
    "Claim #": pick(selectedFields, ["ClaimID", "ClaimId", "ClaimNumber"], ""),
    "Insurance(Pri)": pick(selectedFields, ["InsurancePrimary", "PrimaryInsurance"], ""),
    "Insurance(Sec)": pick(selectedFields, ["InsuranceSecondary", "SecondaryInsurance"], ""),
    Balance: pick(selectedFields, ["OutstandingBalance", "Balance", "Amount"], ""),
  };
  return formatColumnValue(pick(item, column.keys || [], fallbackByLabel[column.label] ?? ""), column.type);
};

const createDefaultBillingLine = (date = new Date().toISOString().slice(0, 10)) => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  FromDate: date,
  ToDate: date,
  CPT: "",
  CPTDesc: "",
  Modifier1: "",
  ICD1: "",
  ICD2: "",
  ICD3: "",
  ICD4: "",
  Unit: 1,
  UnitCharge: "0.00",
  TotalCharges: "0.00",
});

const parseJsonResult = async (response) => {
  const text = await response.text();
  if (!text) return null;

  let parsed = text;

  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }

  return parsed;
};

const normalizeApiResponse = (payload) => {
  if (!payload) return payload;
  if (payload.d) return normalizeApiResponse(payload.d);
  if (payload.Data) return normalizeApiResponse(payload.Data);
  if (payload.data) return normalizeApiResponse(payload.data);
  if (payload.Result) return normalizeApiResponse(payload.Result);
  if (payload.Results) return normalizeApiResponse(payload.Results);
  if (payload.Response) return normalizeApiResponse(payload.Response);
  if (payload.Payload) return normalizeApiResponse(payload.Payload);
  return payload;
};

const parseNestedJson = (value) => {
  let parsed = value;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }
  return parsed;
};

const getStatusBadgeStyle = (status) => {
  switch (status) {
    case "Pending Review":
      return { background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };
    case "Approved":
      return { background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0" };
    case "Rejected":
      return { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" };
    case "Generated":
      return { background: "#dbeafe", color: "#1e3a8a", border: "1px solid #bfdbfe" };
    default:
      return { background: "#e2e8f0", color: "#334155", border: "1px solid #cbd5e1" };
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatDateInput = (value) => {
  if (!value || value === "-") return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const parseDateDay = (value) => {
  if (!value || value === "-") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const isRowInDateRange = (row, startDate, endDate) => {
  const rowDate = parseDateDay(getRowValue(row, ["FromServiceDate", "from_service_date", "FromDate", "ServiceDate", "DOS"], ""));
  if (rowDate === null) return true;
  const from = parseDateDay(startDate);
  const to = parseDateDay(endDate);
  return (from === null || rowDate >= from) && (to === null || rowDate <= to);
};

const toLedgerDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const PatientStatementsWorkflow = () => {
  const reduxUser = useSelector((state) => state.user.user);
  const reduxToken = useSelector((state) => state.user.token);
  const userData = reduxUser || (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "null");
    } catch {
      return null;
    }
  })();
  const token = reduxToken || localStorage.getItem("sAuthToken");
  const practiceID = userData?.practiceID || userData?.PracticeID || userData?.PracticeId || 0;
  const billingUserId = userData?.userID || userData?.UserID || userData?.UserId || 0;
  const billingUserName =
    userData?.Username ||
    userData?.UserName ||
    userData?.FullName ||
    userData?.Name ||
    [userData?.FirstName, userData?.LastName].filter(Boolean).join(" ");

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ pendingReview: 0, approved: 0, rejected: 0, generated: 0 });
  const [workflowList, setWorkflowList] = useState([]);
  const [requestHistory, setRequestHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [filterStatus, setFilterStatus] = useState("Pending Review");
  const initialRange = useMemo(defaultIsoRange, []);
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [searchText, setSearchText] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [generatedFileName, setGeneratedFileName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [patientSearchText, setPatientSearchText] = useState("");
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [requestAccountNumber, setRequestAccountNumber] = useState("");
  const [requestBalance, setRequestBalance] = useState("");
  const [requestDos, setRequestDos] = useState("");
  const [requestStatementFromDate, setRequestStatementFromDate] = useState(initialRange.from);
  const [requestStatementToDate, setRequestStatementToDate] = useState(initialRange.to);
  const [requestNotes, setRequestNotes] = useState("");
  const [editingStatementRequestId, setEditingStatementRequestId] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [pdfViewer, setPdfViewer] = useState({ isOpen: false, url: "", title: "Document Preview" });
  const [ledgerRows, setLedgerRows] = useState([]);
  const [claimRows, setClaimRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [bottomDeskTab, setBottomDeskTab] = useState("frontDesk");
  const [frontDeskTab, setFrontDeskTab] = useState("statementBilling");
  const [billingDeskTab, setBillingDeskTab] = useState("requestDetails");
  const [tableFilters, setTableFilters] = useState({});
  const [tableSorts, setTableSorts] = useState({});
  const [selectedStatementIds, setSelectedStatementIds] = useState([]);
  const [frontDeskRows, setFrontDeskRows] = useState([]);
  const [frontDeskCurrentRows, setFrontDeskCurrentRows] = useState([]);
  const [frontDeskCopay, setFrontDeskCopay] = useState("");
  const [frontDeskTotalBalance, setFrontDeskTotalBalance] = useState("");
  const [frontDeskLoading, setFrontDeskLoading] = useState(false);
  const [frontDeskError, setFrontDeskError] = useState("");
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [billingDos, setBillingDos] = useState(new Date().toISOString().slice(0, 10));
  const [billingPos, setBillingPos] = useState("0");
  const [paymentAmount, setPaymentAmount] = useState("0.00");
  const [useCopayPayment, setUseCopayPayment] = useState(false);
  const [paymentMode, setPaymentMode] = useState("0");
  const [paymentSplits, setPaymentSplits] = useState([
    { id: "payment-1", amount: "0.00", mode: "0", paymentReferenceNumber: "", chequeDate: "" },
  ]);
  const [paymentReferenceNumber, setPaymentReferenceNumber] = useState("");
  const [chequeDate, setChequeDate] = useState("");
  const [ccOrChequeLabel, setCcOrChequeLabel] = useState("");
  const [completePatientStatement, setCompletePatientStatement] = useState(false);

  const requestFilter = useMemo(
    () => ({
      status: filterStatus === "All" ? "" : filterStatus,
      fromDate,
      toDate,
      searchText: searchText.trim(),
    }),
    [fromDate, toDate, filterStatus, searchText]
  );

  useEffect(() => {
    if (!token) return;
    fetchWorkflowSummary();
    fetchWorkflowList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestFilter, token]);

  useEffect(() => {
    const availableIds = new Set(workflowList.map((item) => String(getRequestId(item))).filter(Boolean));
    setSelectedStatementIds((current) => current.filter((id) => availableIds.has(id)));
  }, [workflowList]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!pdfViewer.isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPdfViewer({ isOpen: false, url: "", title: "Document Preview" });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pdfViewer.isOpen]);

  useEffect(() => {
    if (useCopayPayment) {
      const copayAmount = parseAmount(frontDeskCopay).toFixed(2);
      setPaymentAmount(copayAmount);
      setPaymentSplits((current) => {
        const [first = { id: "payment-1", mode: "0", paymentReferenceNumber: "", chequeDate: "" }, ...rest] = current;
        return [{ ...first, amount: copayAmount }, ...rest];
      });
    }
  }, [useCopayPayment, frontDeskCopay]);

  const selectedFields = selectedRequest || {};
  const selectedStatus = getStatus(selectedRequest);
  const selectedPatientId = pick(selectedFields, ["PatientID", "PatientId", "patientID", "patientId", "PatientID_FK"], "");
  const selectedPatientName = pick(selectedFields, ["PatientName", "Patient", "Name"], "");
  const selectedAccountNumber = pick(selectedFields, ["AccountNumber", "Account", "AccountNo"], "");
  const displayedOutstandingBalance = pick(selectedFields, ["OutstandingBalance", "Balance", "Amount"], 0);
  const outstandingBalance = parseAmount(displayedOutstandingBalance);
  const billingBalance = frontDeskTotalBalance || outstandingBalance;
  const canReview = selectedRequest && !["Approved", "Rejected", "Generated"].includes(selectedStatus);
  const canMarkGenerated = selectedRequest && selectedStatus === "Approved";
  const canGenerateInvoice = selectedRequest && selectedStatus === "Approved";
  const selectedReviewNotes = pick(selectedFields, ["BillingReviewNotes", "ReviewNotes", "ApprovalNotes", "ApproveNotes", "RejectNotes"], "");
  const selectedReviewedBy = pick(selectedFields, ["BillingReviewedByName", "ReviewedByName", "ApprovedBy", "RejectedBy"], "");
  const selectedStatementFromDate = pick(selectedFields, ["StatementFromDate", "FromDate", "StatementStartDate"], "");
  const selectedStatementToDate = pick(selectedFields, ["StatementToDate", "ToDate", "StatementEndDate"], "");
  const filteredFrontDeskRows = frontDeskRows.filter((row) => isRowInDateRange(row, fromDate, toDate));

  const getTableFilter = (tableKey, columnKey = "__global") => tableFilters[tableKey]?.[columnKey] || "";
  const getTableSort = (tableKey) => tableSorts[tableKey] || { key: "", direction: "asc" };

  const setTableFilter = (tableKey, columnKey, value) => {
    setTableFilters((current) => ({
      ...current,
      [tableKey]: {
        ...(current[tableKey] || {}),
        [columnKey]: value,
      },
    }));
  };

  const toggleTableSort = (tableKey, key) => {
    setTableSorts((current) => {
      const active = current[tableKey] || {};
      return {
        ...current,
        [tableKey]: {
          key,
          direction: active.key === key && active.direction === "asc" ? "desc" : "asc",
        },
      };
    });
  };

  const normalizeSortValue = (value) => {
    if (value === undefined || value === null) return "";
    const amount = Number(String(value).replace(/[$,]/g, ""));
    if (Number.isFinite(amount) && String(value).trim() !== "") return amount;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime()) && /[-/]/.test(String(value))) return date.getTime();
    return String(value).toLowerCase();
  };

  const getTableCellValue = (row, column, index) => {
    if (column.value) return column.value(row, index);
    if (column.getValue) return column.getValue(row, index);
    if (column.keys) return getRowValue(row, column.keys, "");
    return "";
  };

  const applyTableView = (rows, tableKey, columns) => {
    const filters = tableFilters[tableKey] || {};
    const globalFilter = String(filters.__global || "").trim().toLowerCase();
    const columnFilters = Object.entries(filters)
      .filter(([key, value]) => key !== "__global" && String(value || "").trim())
      .map(([key, value]) => [key, String(value).trim().toLowerCase()]);
    const sort = getTableSort(tableKey);
    const indexedRows = rows.map((row, index) => ({ row, index }));
    const filteredRows = globalFilter || columnFilters.length
      ? indexedRows.filter(({ row, index }) =>
          (!globalFilter || columns.some((column) => String(getTableCellValue(row, column, index)).toLowerCase().includes(globalFilter))) &&
          columnFilters.every(([key, value]) => {
            const column = columns.find((item) => item.key === key || item.label === key);
            return !column || String(getTableCellValue(row, column, index)).toLowerCase().includes(value);
          })
        )
      : indexedRows;

    if (!sort.key) return filteredRows.map(({ row }) => row);

    const sortColumn = columns.find((column) => column.key === sort.key || column.label === sort.key);
    if (!sortColumn) return filteredRows.map(({ row }) => row);

    return [...filteredRows]
      .sort((a, b) => {
        const left = normalizeSortValue(getTableCellValue(a.row, sortColumn, a.index));
        const right = normalizeSortValue(getTableCellValue(b.row, sortColumn, b.index));
        if (left < right) return sort.direction === "asc" ? -1 : 1;
        if (left > right) return sort.direction === "asc" ? 1 : -1;
        return a.index - b.index;
      })
      .map(({ row }) => row);
  };

  const SortableHeader = ({ tableKey, column, children }) => {
    const sort = getTableSort(tableKey);
    const sortKey = column.key || column.label || children;
    const isActive = sort.key === sortKey;
    return (
      <th className="psw-sortable-th">
        <div className="psw-column-head">
          <button type="button" onClick={() => toggleTableSort(tableKey, sortKey)}>
            <span>{children || column.label}</span>
            <span className="psw-sort-icon">{isActive ? (sort.direction === "asc" ? "up" : "down") : "sort"}</span>
          </button>
          {!column.disableFilter && (
            <input
              type="search"
              className="psw-column-filter"
              placeholder="Search"
              value={getTableFilter(tableKey, sortKey)}
              onChange={(e) => setTableFilter(tableKey, sortKey, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      </th>
    );
  };

  const workflowColumns = [
    { key: "Request ID", label: "Request ID", getValue: (item) => getRequestId(item) },
    { key: "Patient Name", label: "Patient Name", keys: ["PatientName", "Patient", "Name"] },
    { key: "Account #", label: "Account #", keys: ["AccountNumber", "Account", "AccountNo"] },
    { key: "Outstanding Balance", label: "Outstanding Balance", keys: ["OutstandingBalance", "Balance", "Amount"] },
    { key: "From Date", label: "From Date", getValue: (item) => formatDateOnly(pick(item, ["StatementFromDate", "FromDate", "StatementStartDate"], "")) },
    { key: "To Date", label: "To Date", getValue: (item) => formatDateOnly(pick(item, ["StatementToDate", "ToDate", "StatementEndDate"], "")) },
    { key: "Requested By", label: "Requested By", keys: ["RequestedByName", "RequestedBy", "CreatedBy", "requestedBy"] },
    { key: "Request Date", label: "Request Date", getValue: (item) => formatDateTime(pick(item, ["RequestDate", "CreatedOn", "Date"], "")) },
    { key: "Status", label: "Status", getValue: (item) => getStatus(item) },
  ];

  const requestHistoryColumns = [
    { key: "Request ID", label: "Request ID", getValue: (item) => getRequestId(item) },
    { key: "Patient Name", label: "Patient Name", keys: ["PatientName", "Patient", "Name"] },
    { key: "Account #", label: "Account #", keys: ["AccountNumber", "Account", "AccountNo"] },
    { key: "Outstanding Balance", label: "Outstanding Balance", keys: ["OutstandingBalance", "Balance", "Amount"] },
    { key: "DOS", label: "DOS", getValue: (item) => formatDateOnly(pick(item, ["DOS", "ServiceDate"], "")) },
    { key: "From Date", label: "From Date", getValue: (item) => formatDateOnly(pick(item, ["StatementFromDate", "FromDate", "StatementStartDate"], "")) },
    { key: "To Date", label: "To Date", getValue: (item) => formatDateOnly(pick(item, ["StatementToDate", "ToDate", "StatementEndDate"], "")) },
    { key: "Requested By", label: "Requested By", keys: ["RequestedByName", "RequestedBy", "CreatedBy"] },
    { key: "Request Date", label: "Request Date", getValue: (item) => formatDateTime(pick(item, ["RequestDate", "CreatedDate", "CreatedOn", "Date"], "")) },
    { key: "Status", label: "Status", getValue: (item) => getStatus(item) },
    { key: "Request Notes", label: "Request Notes", keys: ["Notes", "RequestNotes", "Comments"] },
    { key: "Review Notes", label: "Review Notes", keys: ["BillingReviewNotes", "ReviewNotes", "ApprovalNotes", "ApproveNotes", "RejectNotes"] },
    { key: "Reviewed By", label: "Reviewed By", keys: ["BillingReviewedByName", "ReviewedByName", "ApprovedBy", "RejectedBy"] },
  ];

  const frontDeskBillingColumns = [
    { key: "Invoice Number", label: "Invoice Number", keys: ["PracticeInvoiceNumber", "practiceinvoiceNumber", "InvoiceNumber", "invoiceNumber"] },
    { key: "From Date", label: "From Date", getValue: (row) => formatDateOnly(getRowValue(row, ["FromServiceDate", "from_service_date", "FromDate"], "")) },
    { key: "To Date", label: "To Date", getValue: (row) => formatDateOnly(getRowValue(row, ["ToServiceDate", "to_service_date", "ToDate"], "")) },
    { key: "Status", label: "Status", keys: ["Status", "status"] },
    { key: "Amount Paid", label: "Amount Paid", keys: ["AmountPaid", "amountPaid", "paidamountString"] },
    { key: "Payment Mode", label: "Payment Mode", keys: ["PaymentMode", "paymentMode", "paymentmodestring"] },
    { key: "Payment Reference Number", label: "Payment Reference Number", keys: ["PaymentReferenceNumber", "paymentReferenceNumber", "PaymentRefNo"] },
    { key: "Receipt", label: "Receipt", getValue: (row) => getRowValue(row, ["ReceiptUrl", "PDFURL"], "") ? "View Regenerate" : "Regenerate" },
    { key: "Balance", label: "Balance", keys: ["StatementBalance", "LedgerBalance", "Balance", "balancedue"] },
  ];

  const frontDeskLedgerColumns = [
    ...frontDeskBillingColumns.slice(0, 7),
    { key: "Receipt", label: "Receipt", getValue: (row) => getRowValue(row, ["ReceiptUrl", "PDFURL"], "") ? "View Regenerate" : "Regenerate" },
    { key: "Balance", label: "Balance", keys: ["StatementBalance", "LedgerBalance", "Balance", "balancedue"] },
    { key: "Statement", label: "Statement", getValue: (row) => getRowValue(row, ["StatementUrl", "PDFStatURL"], "") ? "View Regenerate" : "Regenerate" },
  ];

  const transactionColumns = [
    { key: "#", label: "#", value: (_item, index) => index + 1 },
    { key: "DOS", label: "DOS", getValue: (item) => formatDateOnly(pick(item, ["DOS", "ClaimDOS"], "")) },
    { key: "DOT", label: "DOT", getValue: (item) => formatDateOnly(pick(item, ["PostingDate", "DOT"], "")) },
    { key: "Claim ID", label: "Claim ID", keys: ["ClaimID", "ClaimId"] },
    { key: "CPT", label: "CPT", keys: ["CPT", "CPTOld", "CPTCode"] },
    { key: "Transaction Code", label: "Transaction Code", keys: ["TransactionCode"] },
    { key: "Transaction", label: "Transaction", keys: ["TransactionCodeDescription", "TransactionDescription", "Transaction"] },
    { key: "Total Amt", label: "Total Amt", keys: ["TransactionAmt", "TotalAmt", "Amount"] },
    { key: "Insurance Amt", label: "Insurance Amt", keys: ["InsuranceAmtmoney", "InsuranceAmt"] },
    { key: "Patient Amt", label: "Patient Amt", keys: ["PatientAmtmoney", "PatientAmt"] },
    { key: "DOP", label: "DOP", getValue: (item) => formatDateOnly(pick(item, ["PostingDate", "DOP"], "")) },
  ];

  const visibleWorkflowList = applyTableView(workflowList, "workflow", workflowColumns);
  const visibleRequestHistory = applyTableView(requestHistory, "requestHistory", requestHistoryColumns);
  const visibleFrontDeskBillingRows = applyTableView(frontDeskCurrentRows, "frontDeskBilling", frontDeskBillingColumns);
  const visibleFrontDeskRows = applyTableView(filteredFrontDeskRows, "frontDeskLedger", frontDeskLedgerColumns);
  const visibleClaimRows = applyTableView(claimRows, "billingClaims", billingDeskClaimColumns);
  const visibleLedgerRows = applyTableView(ledgerRows, "billingTransactions", transactionColumns);
  const visibleWorkflowIds = visibleWorkflowList.map((item) => String(getRequestId(item))).filter(Boolean);
  const selectedWorkflowRequests = workflowList.filter((item) => selectedStatementIds.includes(String(getRequestId(item))));
  const allVisibleWorkflowSelected = visibleWorkflowIds.length > 0 && visibleWorkflowIds.every((id) => selectedStatementIds.includes(id));

  const toggleStatementSelection = (requestId) => {
    const key = String(requestId || "");
    if (!key) return;
    setSelectedStatementIds((current) =>
      current.includes(key) ? current.filter((id) => id !== key) : [...current, key]
    );
  };

  const toggleVisibleStatementSelection = () => {
    setSelectedStatementIds((current) => {
      const visibleSet = new Set(visibleWorkflowIds);
      if (allVisibleWorkflowSelected) return current.filter((id) => !visibleSet.has(id));
      return Array.from(new Set([...current, ...visibleWorkflowIds]));
    });
  };

  useEffect(() => {
    if (!selectedPatientId) {
      setLedgerRows([]);
      setClaimRows([]);
      setFrontDeskRows([]);
      setFrontDeskCurrentRows([]);
      setFrontDeskCopay("");
      setFrontDeskTotalBalance("");
      setRequestHistory([]);
      setHistoryError("");
      setLedgerError(selectedRequest ? "Patient ID is not available for this request." : "");
      return;
    }
    fetchLedgerData(selectedPatientId);
    fetchFrontDeskDashboard(selectedPatientId);
    fetchRequestHistory(selectedPatientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId]);

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
  };

  const buildQuery = (input) => {
    const params = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") params.append(key, value);
    });
    const query = params.toString();
    return query ? `?${query}` : "";
  };

  const fetchPatientSuggestions = async (text) => {
    setPatientSearchText(text);
    setSelectedPatient(null);
    if (!text || text.trim().length < 2 || !practiceID) {
      setPatientSuggestions([]);
      return;
    }

    try {
      const sJSONInput = JSON.stringify({
        PracticeID: practiceID,
        SearchKey: text.trim(),
        SearchBy: 0,
        isFromLogin: 0,
      });
      const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllPatientNames}?sJSONInput=${encodeURIComponent(sJSONInput)}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`Patient search failed (${res.status})`);
      const parsed = normalizeApiResponse(await parseJsonResult(res));
      const list = Array.isArray(parsed) ? parsed : parsed?.Data || [];
      setPatientSuggestions(
        Array.isArray(list)
          ? list.map((patient) => ({
              label: getPatientName(patient),
              val: patient.PatientID || patient.PatientId || patient.patientID || patient.patientId,
              fullData: patient,
            }))
          : []
      );
    } catch (err) {
      console.error(err);
      setPatientSuggestions([]);
    }
  };

  const selectPatient = (suggestion) => {
    const patient = suggestion.fullData || {};
    setSelectedPatient({ ...patient, PatientID: suggestion.val, PatientName: suggestion.label });
    setPatientSearchText(suggestion.label);
    setRequestAccountNumber(patient.AccountNumber || patient.AccountNo || patient.MRN || patient.PatientMRN || "");
    setPatientSuggestions([]);
    fetchFrontDeskDashboard(suggestion.val, { populateRequestBalance: true });
  };

  const populateRequestFormFromStatement = (request) => {
    if (!request) return;
    const requestId = getRequestId(request);
    const patientId = pick(request, ["PatientID", "PatientId", "patientID", "patientId", "PatientID_FK"], "");
    const patientName = pick(request, ["PatientName", "Patient", "Name"], "");
    setEditingStatementRequestId(requestId || "");
    setSelectedPatient({
      ...request,
      PatientID: patientId,
      PatientId: patientId,
      PatientName: patientName,
    });
    setPatientSearchText(patientName || "");
    setPatientSuggestions([]);
    setRequestAccountNumber(pick(request, ["AccountNumber", "Account", "AccountNo", "PatientMRN", "MRN"], ""));
    setRequestBalance(parseAmount(pick(request, ["OutstandingBalance", "Balance", "Amount"], 0)).toFixed(2));
    setRequestDos(formatDateInput(pick(request, ["DOS", "ServiceDate"], "")));
    setRequestStatementFromDate(formatDateInput(pick(request, ["StatementFromDate", "FromDate", "StatementStartDate"], "")) || initialRange.from);
    setRequestStatementToDate(formatDateInput(pick(request, ["StatementToDate", "ToDate", "StatementEndDate"], "")) || initialRange.to);
    setRequestNotes(pick(request, ["Notes", "RequestNotes", "Comments"], ""));
    if (patientId) fetchFrontDeskDashboard(patientId);
  };

  const clearRequestForm = () => {
    setPatientSearchText("");
    setPatientSuggestions([]);
    setSelectedPatient(null);
    setRequestAccountNumber("");
    setRequestBalance("");
    setRequestDos("");
    setRequestStatementFromDate(initialRange.from);
    setRequestStatementToDate(initialRange.to);
    setRequestNotes("");
    setEditingStatementRequestId("");
  };

  const handleCreateRequest = async () => {
    const patientId = selectedPatient?.PatientID || selectedPatient?.PatientId || selectedPatient?.patientID || selectedPatient?.patientId;
    if (!patientId) {
      showToast("warning", "Patient required", "Please select a patient from the search list.");
      return;
    }
    if (!requestBalance) {
      showToast("warning", "Balance required", "Please enter outstanding balance.");
      return;
    }

    setCreateLoading(true);
    setError("");
    try {
      const body = {
        PatientId: Number(patientId),
        ClaimId: null,
        AccountNumber: requestAccountNumber || "",
        PatientName: getPatientName(selectedPatient) || patientSearchText,
        DOS: requestDos || null,
        StatementFromDate: requestStatementFromDate || null,
        StatementToDate: requestStatementToDate || null,
        FromDate: requestStatementFromDate || null,
        ToDate: requestStatementToDate || null,
        Location: selectedPatient?.Location || selectedPatient?.LocationName || "",
        InsurancePrimary: selectedPatient?.InsurancePrimary || selectedPatient?.PrimaryInsurance || "",
        InsuranceSecondary: selectedPatient?.InsuranceSecondary || selectedPatient?.SecondaryInsurance || "",
        OutstandingBalance: parseAmount(requestBalance),
        RequestedByUserId: Number(billingUserId) || 0,
        RequestedByName: billingUserName || "",
        RequestNotes: requestNotes || "",
      };
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.RequestFromLedger}`;
      const res = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Create request failed (${res.status})`);
      await parseJsonResult(res);
      clearRequestForm();
      setFilterStatus("All");
      await fetchWorkflowSummary();
      await fetchWorkflowList();
      showToast("success", "Request created", "Statement request has been added to the workflow.");
    } catch (err) {
      console.error(err);
      showToast("error", "Request not created", "Unable to create statement request. " + err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const fetchWorkflowSummary = async () => {
    setLoading(true);
    setError("");
    try {
      // Simple GET endpoint - no query parameters needed
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GetStatementWorkflowSummary}`;
      const res = await fetchWithAuth(url, { method: "GET" });
      if (!res.ok) throw new Error(`Summary failed (${res.status})`);

      const parsed = await parseJsonResult(res);
      const data = normalizeApiResponse(parsed) || {};

      const summary = {
        pendingReview: data?.PendingReviewCount ?? data?.pendingReview ?? data?.pending ?? 0,
        approved: data?.ApprovedCount ?? data?.approved ?? data?.approvedCount ?? 0,
        rejected: data?.RejectedCount ?? data?.rejected ?? data?.rejectedCount ?? 0,
        generated: data?.GeneratedCount ?? data?.generated ?? data?.generatedCount ?? 0,
      };
      setSummary(summary);
    } catch (err) {
      console.error("Error fetching workflow summary:", err);
      setError("Unable to load workflow summary: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowList = async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GetStatementWorkflowList}${buildQuery(requestFilter)}`;
      const res = await fetchWithAuth(url, { method: "GET" });
      if (!res.ok) throw new Error(`Workflow list failed (${res.status})`);
      const parsed = await parseJsonResult(res);
      const data = normalizeApiResponse(parsed);
      setWorkflowList(
        Array.isArray(data)
          ? data
          : data?.items || data?.WorkflowList || data?.workflowList || data?.List || data?.Requests || data?.requestList || data?.RequestList || []
      );
    } catch (err) {
      console.error(err);
      setError("Unable to load workflow requests.");
      setWorkflowList([]);
    } finally {
      setLoading(false);
    }
  };

  const matchesSelectedPatient = (request, patientId) => {
    const requestPatientId = pick(request, ["PatientID", "PatientId", "patientID", "patientId", "PatientID_FK"], "");
    if (requestPatientId && String(requestPatientId) === String(patientId)) return true;

    const requestAccount = pick(request, ["AccountNumber", "Account", "AccountNo"], "");
    if (selectedAccountNumber && requestAccount && String(requestAccount).toLowerCase() === String(selectedAccountNumber).toLowerCase()) return true;

    const requestPatientName = pick(request, ["PatientName", "Patient", "Name"], "");
    return Boolean(selectedPatientName && requestPatientName && String(requestPatientName).toLowerCase() === String(selectedPatientName).toLowerCase());
  };

  const fetchRequestHistory = async (patientId = selectedPatientId) => {
    if (!patientId) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GetStatementRequests}`;
      const res = await fetchWithAuth(url, { method: "GET" });
      if (!res.ok) throw new Error(`Request history failed (${res.status})`);
      const data = normalizeApiResponse(await parseJsonResult(res));
      const list = Array.isArray(data) ? data : data?.Requests || data?.RequestList || data?.items || [];
      setRequestHistory(Array.isArray(list) ? list.filter((item) => matchesSelectedPatient(item, patientId)) : []);
    } catch (err) {
      console.error(err);
      setRequestHistory([]);
      setHistoryError("Unable to load statement request history. " + err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadRequestDetails = async (requestId, baseRequest = null) => {
    const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GetStatementById.replace("{id}", encodeURIComponent(requestId))}`;
    const res = await fetchWithAuth(url, { method: "GET" });
    if (!res.ok) throw new Error(`Details failed (${res.status})`);
    const parsed = await parseJsonResult(res);
    const data = normalizeApiResponse(parsed);
    const details = Array.isArray(data) ? data[0] : data;
    return mergeDefined(baseRequest || {}, details || {});
  };

  const ensureRequestDetails = async (request) => {
    const requestId = getRequestId(request);
    if (!requestId) return request;
    const hasPatientId = pick(request, ["PatientID", "PatientId", "patientID", "patientId", "PatientID_FK"], "");
    return hasPatientId ? request : loadRequestDetails(requestId, request);
  };

  const fetchRequestDetails = async (requestId, baseRequest = null, options = {}) => {
    try {
      if (baseRequest) setSelectedRequest(baseRequest);
      const merged = await loadRequestDetails(requestId, baseRequest);
      setSelectedRequest(merged);
      setApproveNotes(pick(merged, ["BillingReviewNotes", "ReviewNotes", "ApprovalNotes", "ApproveNotes"], ""));
      setRejectNotes(getStatus(merged) === "Rejected" ? pick(merged, ["BillingReviewNotes", "RejectNotes", "RejectionNotes"], "") : "");
      if (options.showInFrontDesk || bottomDeskTab === "frontDesk") {
        setBottomDeskTab("frontDesk");
        setFrontDeskTab("statementBilling");
        populateRequestFormFromStatement(merged);
      }
    } catch (err) {
      console.error(err);
      setError("Unable to load selected request details.");
    }
  };

  const buildBillingUserContext = (patientIdOverride = selectedPatientId, fieldOverride = selectedFields) => ({
    PatientId: Number(patientIdOverride) || 0,
    PracticeId: Number(practiceID) || 0,
    UserId: Number(billingUserId) || 0,
    RoleId: Number(userData?.roleID || userData?.RoleID || userData?.RoleId) || 0,
    ProviderId: Number(userData?.providerID || userData?.ProviderID || userData?.ProviderId) || 0,
    LocationId: Number(userData?.LocationID || userData?.locationID || userData?.LocationId) || 0,
    EncounterId: Number(pick(fieldOverride, ["EncounterID", "EncounterId", "iEncounterID"], 0)) || 0,
    PatientName: pick(fieldOverride, ["PatientName", "Patient", "Name"], "") === "-" ? "" : pick(fieldOverride, ["PatientName", "Patient", "Name"], selectedPatientName),
    PatientMRN: pick(fieldOverride, ["PatientMRN", "MRN", "AccountNumber", "Account", "AccountNo"], ""),
    PracticeName: userData?.practiceName || userData?.PracticeName || "",
    PracticeFullId: userData?.practiceFullID || userData?.PracticeFullID || "",
    UserFullName: billingUserName || "",
    UserEPIN: userData?.userEPIN || userData?.UserEPIN || "",
    CurrentWorkingDate: billingDos || new Date().toISOString(),
    UserTimeZoneOffSet: Number(userData?.userTimeZoneOffSet || userData?.DefaultTimeZoneOffSet || 0),
    DefaultBillingProvider: userData?.DefaultBillingProvider || userData?.defaultBillingProvider || "",
  });

  const fetchFrontDeskDashboard = async (patientId = selectedPatientId, options = {}) => {
    if (!patientId) return;
    setFrontDeskLoading(true);
    setFrontDeskError("");
    try {
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GetFrontDeskDashboard}`;
      const res = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(buildBillingUserContext(patientId)),
      });
      if (!res.ok) throw new Error(`Front desk ledger failed (${res.status})`);
      const data = normalizeApiResponse(await parseJsonResult(res)) || {};
      setFrontDeskRows(Array.isArray(data.LedgerRows) ? data.LedgerRows : []);
      setFrontDeskCurrentRows(Array.isArray(data.CurrentDateLedgerRows) ? data.CurrentDateLedgerRows : []);
      setFrontDeskCopay(data.PatCopay || "");
      setFrontDeskTotalBalance(data.TotalBalanceDue || "");
      if (options.populateRequestBalance) {
        setRequestBalance(parseAmount(data.TotalBalanceDue).toFixed(2));
      }
      if (useCopayPayment) {
        setPaymentAmount(parseAmount(data.PatCopay).toFixed(2));
      }
    } catch (err) {
      console.error(err);
      setFrontDeskRows([]);
      setFrontDeskCurrentRows([]);
      setFrontDeskCopay("");
      setFrontDeskTotalBalance("");
      setFrontDeskError("Unable to load front desk ledger. " + err.message);
    } finally {
      setFrontDeskLoading(false);
    }
  };

  const mapBillingLines = () => [];

  const readDocumentActionResult = async (res) => {
    const payload = normalizeApiResponse(await parseJsonResult(res));
    if (!res.ok || payload?.Success === false) {
      throw new Error(payload?.Message || `Document action failed (${res.status})`);
    }
    return payload || {};
  };

  const resolveDocumentUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url) || /^file:\/\//i.test(url)) return url;
    if (url.startsWith("/")) return `${APIConstants.BASE_URL_BILLING.replace(/\/$/, "")}${url}`;
    return url;
  };

  const closePdfViewer = () => {
    setPdfViewer({ isOpen: false, url: "", title: "Document Preview" });
  };

  const openDocumentUrl = (url, title = "Document Preview") => {
    const resolvedUrl = resolveDocumentUrl(url);
    if (resolvedUrl) setPdfViewer({ isOpen: true, url: resolvedUrl, title });
  };

  const hasClaimIdentity = (item) => Boolean(
    pick(item, ["ClaimID", "ClaimId", "claimID", "claimId", "claimid", "ClaimNumber", "claimNumber"], "") ||
    pick(item, ["DOS", "dos", "ClaimDOS", "claimDOS", "ServiceDate", "serviceDate", "DateOfService"], "") ||
    pick(item, ["Location", "location", "LocationNickname", "locationNickname", "LocationNickName"], "") ||
    pick(item, ["InsCompNickName", "insCompNickName", "PrimaryInsurance", "primaryInsurance", "Payer", "payer"], "")
  );

  const getPaymentModeText = (mode) => (
    mode === "0" ? "Cash" : mode === "1" ? "Cheque" : mode === "2" ? "CC" : mode === "5" ? "Debit Card" : "No Payment"
  );

  const getPaymentReferenceLabel = (mode) => (
    mode === "1" ? "Cheque #" : mode === "2" || mode === "5" ? "Approval #" : ""
  );

  const normalizePaymentSplits = () =>
    paymentSplits
      .map((split, index) => {
        const mode = split.mode || "0";
        const referenceLabel = getPaymentReferenceLabel(mode);
        return {
          ...split,
          id: split.id || `payment-${index + 1}`,
          amountValue: parseAmount(split.amount),
          mode,
          modeText: getPaymentModeText(mode),
          paymentReferenceNumber: referenceLabel ? (split.paymentReferenceNumber || "") : "",
          chequeDate: mode === "1" ? (split.chequeDate || "") : "",
          ccOrChequeLabel: referenceLabel,
        };
      })
      .filter((split) => split.amountValue > 0);

  const getSplitPaymentTotal = () =>
    normalizePaymentSplits().reduce((total, split) => total + split.amountValue, 0);

  const updatePaymentSplit = (id, changes) => {
    setPaymentSplits((current) =>
      current.map((split) => {
        if (split.id !== id) return split;
        const next = { ...split, ...changes };
        if (Object.prototype.hasOwnProperty.call(changes, "mode")) {
          const referenceLabel = getPaymentReferenceLabel(next.mode);
          if (!referenceLabel) next.paymentReferenceNumber = "";
          if (next.mode !== "1") next.chequeDate = "";
        }
        return next;
      })
    );
  };

  const addPaymentSplit = () => {
    setPaymentSplits((current) => [
      ...current,
      {
        id: `payment-${Date.now()}-${current.length}`,
        amount: "",
        mode: "0",
        paymentReferenceNumber: "",
        chequeDate: "",
      },
    ]);
  };

  const removePaymentSplit = (id) => {
    setPaymentSplits((current) => {
      if (current.length === 1) {
        return [{ ...current[0], amount: "", paymentReferenceNumber: "", chequeDate: "" }];
      }
      return current.filter((split) => split.id !== id);
    });
  };

  const billingPaymentTotal = getSplitPaymentTotal();

  const getCurrentAuthToken = () => token || localStorage.getItem("sAuthToken") || "";

  const handlePaymentModeChange = (mode) => {
    setPaymentMode(mode);
    if (mode !== "1" && mode !== "2" && mode !== "5") {
      setPaymentReferenceNumber("");
      setChequeDate("");
      setCcOrChequeLabel("");
      return;
    }

    setCcOrChequeLabel(getPaymentReferenceLabel(mode));
    if (mode !== "1") {
      setChequeDate("");
    }
  };

  const handleGenerateInvoiceForRequest = async (request = selectedRequest, showSuccessToast = true) => {
    setBillingActionLoading(true);
    try {
      request = await ensureRequestDetails(request);
      const requestPatientId = pick(request, ["PatientID", "PatientId", "patientID", "patientId", "PatientID_FK"], "");
      if (!requestPatientId) {
        showToast("warning", "Patient required", "Selected request does not include a patient id.");
        return false;
      }
      if (!completePatientStatement && (!fromDate || !toDate)) {
        showToast("warning", "Date range required", "Please select From and To dates or choose Complete Patient Statement.");
        return false;
      }

      const statementFromDate = completePatientStatement ? "" : fromDate;
      const statementToDate = completePatientStatement ? "" : toDate;
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GenerateInvoice}`;
      const res = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          UserContext: buildBillingUserContext(requestPatientId, request),
          RenderingProviderId: Number(userData?.providerID || userData?.ProviderID || 0),
          RenderingProviderName: userData?.ProviderName || userData?.providerName || "",
          POS: Number(billingPos) || 0,
          PrimaryInsuranceId: Number(pick(request, ["PrimaryInsuranceId", "PrimaryInsuranceID", "InsurancePrimaryId"], 0)) || 0,
          IsCheckIn: mapBillingLines().length === 0,
          SetDefaultProvider: false,
          PaidAmount1: parseAmount(paymentAmount),
          PaidAmount2: 0,
          PaymentMode1: paymentMode,
          PaymentMode1Text: paymentMode === "0" ? "Cash" : paymentMode === "1" ? "Cheque" : paymentMode === "2" ? "CC" : paymentMode === "5" ? "Debit Card" : "No Payment",
          PaymentMode2: "",
          PaymentMode2Text: "",
          PaymentReferenceNumber: paymentReferenceNumber,
          ChequeDate: chequeDate,
          CCorChequeLabel: ccOrChequeLabel,
          PreviousBalance: parseAmount(pick(request, ["OutstandingBalance", "Balance", "Amount"], 0)),
          FromDate: statementFromDate,
          ToDate: statementToDate,
          CompletePatientStatement: completePatientStatement,
          Lines: mapBillingLines(),
        }),
      });
      const result = await readDocumentActionResult(res);
      await markRequestGenerated(mergeDefined(request, { StatementFilePath: result.InvoiceUrl || result.FilePath || result.FileName || "" }), false);
      if (showSuccessToast) showToast("success", "Invoice generated", result.InvoiceUrl || result.Message || "Invoice was generated.");
      if (showSuccessToast) openDocumentUrl(result.InvoiceUrl, "Patient Statement");
      fetchFrontDeskDashboard(requestPatientId);
      fetchLedgerData(requestPatientId);
      return true;
    } catch (err) {
      console.error(err);
      showToast("error", "Invoice failed", err.message);
      return false;
    } finally {
      setBillingActionLoading(false);
    }
  };

  const handleGenerateInvoice = () => handleGenerateInvoiceForRequest(selectedRequest);

  const handleGenerateReceipt = async () => {
    if (!selectedPatientId) {
      showToast("warning", "Patient required", "Select a request before generating a receipt.");
      return;
    }
    const splitPayments = normalizePaymentSplits();
    const splitPaymentTotal = splitPayments.reduce((total, split) => total + split.amountValue, 0);
    if (splitPaymentTotal <= 0) {
      showToast("warning", "Payment required", "Please enter a payment amount before generating a receipt.");
      return;
    }

    setBillingActionLoading(true);
    try {
      const firstSplit = splitPayments[0];
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.GenerateReceipt}`;
      const res = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          UserContext: buildBillingUserContext(),
          InvoiceNumber: 0,
          PracticeInvoiceNumber: 0,
          RenderingProviderId: Number(userData?.providerID || userData?.ProviderID || 0),
          RenderingProviderName: userData?.ProviderName || userData?.providerName || "",
          POS: Number(billingPos) || 0,
          PrimaryInsuranceId: Number(pick(selectedFields, ["PrimaryInsuranceId", "PrimaryInsuranceID", "InsurancePrimaryId"], 0)) || 0,
          IsCopayPayment: useCopayPayment,
          PaidAmount: splitPaymentTotal,
          PaymentModeText: firstSplit.modeText,
          PaymentModeValue: firstSplit.mode,
          PaymentReferenceNumber: firstSplit.paymentReferenceNumber,
          ChequeDate: firstSplit.chequeDate,
          CCorChequeLabel: firstSplit.ccOrChequeLabel,
          PaymentSplits: splitPayments.map((split) => ({
            PaidAmount: split.amountValue,
            PaymentModeText: split.modeText,
            PaymentModeValue: split.mode,
            PaymentReferenceNumber: split.paymentReferenceNumber,
            ChequeDate: split.chequeDate,
            CCorChequeLabel: split.ccOrChequeLabel,
            IsCopayPayment: useCopayPayment,
          })),
          PreviousBalance: outstandingBalance,
        }),
      });
      const result = await readDocumentActionResult(res);
      showToast("success", "Receipt generated", result.ReceiptUrl || result.Message || "Receipt was generated.");
      openDocumentUrl(result.ReceiptUrl, "Patient Receipt");
      fetchFrontDeskDashboard();
      fetchLedgerData(selectedPatientId);
    } catch (err) {
      console.error(err);
      showToast("error", "Receipt failed", err.message);
    } finally {
      setBillingActionLoading(false);
    }
  };

  const handleRegenerateLedgerDocument = async (row, documentType) => {
    const invoiceNumber = Number(getRowValue(row, ["InvoiceNumber", "invoiceNumber", "billID", "BillID"], 0)) || 0;
    const practiceInvoiceNumber = Number(getRowValue(row, ["PracticeInvoiceNumber", "practiceInvoiceNumber", "InvoiceNo", "Label4"], invoiceNumber)) || invoiceNumber;
    if (!invoiceNumber) {
      showToast("warning", "Invoice missing", "This ledger row does not have an invoice number.");
      return;
    }

    setBillingActionLoading(true);
    try {
      const url = `${APIConstants.BASE_URL_BILLING}${PatientStatementsConstants.RegenerateFromLedger}`;
      const res = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          UserContext: buildBillingUserContext(),
          InvoiceNumber: invoiceNumber,
          PracticeInvoiceNumber: practiceInvoiceNumber,
          AmountPayable: 0,
          RenderingProviderName: userData?.ProviderName || userData?.providerName || "",
          DocumentType: documentType,
        }),
      });
      const result = await readDocumentActionResult(res);
      showToast("success", `${documentType} regenerated`, result.InvoiceUrl || result.ReceiptUrl || result.Message || "Document was regenerated.");
      openDocumentUrl(result.InvoiceUrl || result.ReceiptUrl, documentType === "Receipt" ? "Patient Receipt" : "Patient Statement");
      fetchFrontDeskDashboard();
      fetchLedgerData(selectedPatientId);
    } catch (err) {
      console.error(err);
      showToast("error", "Regenerate failed", err.message);
    } finally {
      setBillingActionLoading(false);
    }
  };

  const fetchLedgerData = async (patientId) => {
    setLedgerLoading(true);
    setLedgerError("");
    let nextError = "";
    try {
      const legacyUser = buildBillingUserContext(patientId);
      const objClaims = {
        iPracticeID: Number(practiceID) || 0,
        sFromDate: toLedgerDate(fromDate),
        sToDate: toLedgerDate(toDate),
        sFilterType: "DOS",
        sPatientID: String(patientId),
        sInsuranceID: "",
        sCPT: "",
        sInsorPatFilter: "pat",
        sLocationID: "0",
        sFilterText: "",
        ViewType: "0",
        sService: [],
        sCPTGroups: [],
        DefaultSearch: true,
        sDefaultSearch: true,
        isClosedClaims: false,
        sClaimStatus: "",
        isMacro: 0,
      };
      const loggedInUserDetails = {
        patientID: Number(patientId) || 0,
        practiceID: legacyUser.PracticeId,
        userID: legacyUser.UserId,
        roleID: legacyUser.RoleId || 7,
        providerID: legacyUser.ProviderId,
        LocationID: String(legacyUser.LocationId || 0),
        iEncounterID: legacyUser.EncounterId,
        patientName: legacyUser.PatientName,
        PatientMRN: legacyUser.PatientMRN,
        practiceName: legacyUser.PracticeName,
        practiceFullID: legacyUser.PracticeFullId,
        userFullName: legacyUser.UserFullName,
        userEPIN: legacyUser.UserEPIN,
        dtCurrentWorkingDate: legacyUser.CurrentWorkingDate,
        userTimeZoneOffSet: legacyUser.UserTimeZoneOffSet,
        DefaultBillingProvider: legacyUser.DefaultBillingProvider,
      };
      const claimInput = JSON.stringify({ ObjClaims: objClaims, LoggedInUserDetails: loggedInUserDetails });
      const claimUrl = `${APIConstants.BASE_URL_BILLING}${ClaimStatusConstants.GetClaimStatusData}?sInputJSON=${encodeURIComponent(claimInput)}`;
      const claimRes = await fetchWithAuth(claimUrl, { method: "GET" });
      if (!claimRes.ok) {
        nextError = `Claims failed (${claimRes.status})`;
        setClaimRows([]);
      } else {
        const claimPayload = await parseJsonResult(claimRes);
        const normalizedClaimPayload = normalizeApiResponse(parseNestedJson(normalizeApiResponse(claimPayload)));
        const claimData =
          claimPayload?.Claims ||
          normalizedClaimPayload?.Claims ||
          claimPayload?.Data?.[0] ||
          normalizedClaimPayload?.Data?.[0] ||
          claimPayload?.data?.[0] ||
          normalizedClaimPayload?.data?.[0] ||
          (Array.isArray(normalizedClaimPayload?.[0]) ? normalizedClaimPayload[0] : normalizedClaimPayload);
        setClaimRows(Array.isArray(claimData) ? claimData.filter(hasClaimIdentity) : []);
      }

      const objInput = {
        DOSFrom: toLedgerDate(fromDate),
        DOSTo: toLedgerDate(toDate),
        PracticeID: practiceID,
        PatientID: patientId,
      };
      const sInputJSON = JSON.stringify({ objPat: objInput });
      const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.GetTransactionData}?sInputJSON=${encodeURIComponent(sInputJSON)}`;
      const transactionHeaders = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      const currentAuthToken = getCurrentAuthToken();
      if (currentAuthToken) {
        transactionHeaders.Authorization = `Bearer ${currentAuthToken}`;
      }
      const res = await fetch(url, {
        method: "GET",
        headers: transactionHeaders,
      });
      if (!res.ok) {
        nextError = [nextError, `Transactions failed (${res.status})`].filter(Boolean).join(". ");
        setLedgerRows([]);
      } else {
        const data = normalizeApiResponse(await parseJsonResult(res));
        setLedgerRows(Array.isArray(data) ? data : []);
      }

      setLedgerError(nextError);
    } catch (err) {
      console.error(err);
      setLedgerRows([]);
      setClaimRows([]);
      setLedgerError("Unable to load ledger information. " + err.message);
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleLedgerSearch = () => {
    if (!selectedPatientId) {
      setLedgerError("Patient ID is not available for this request.");
      return;
    }
    fetchLedgerData(selectedPatientId);
  };

  const updateStatusForRequest = async (request, endpoint, payload, successMessage, showSuccessToast = true) => {
    const requestId = Number(getRequestId(request));
    if (!requestId) return false;
    setActionLoading(true);
    setError("");

    try {
      const url = `${APIConstants.BASE_URL_BILLING}${endpoint}`;
      const body = { StatementRequestId: requestId, ...payload };
      const res = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const result = normalizeApiResponse(await parseJsonResult(res));
      const updatedRequest = mergeDefined(
        request,
        Array.isArray(result) ? result[0] : result,
        body
      );
      if (String(requestId) === String(getRequestId(selectedRequest))) {
        setSelectedRequest(updatedRequest);
        setApproveNotes(pick(updatedRequest, ["BillingReviewNotes", "ReviewNotes", "ApprovalNotes", "ApproveNotes"], ""));
        setRejectNotes(getStatus(updatedRequest) === "Rejected" ? pick(updatedRequest, ["BillingReviewNotes", "RejectNotes", "RejectionNotes"], "") : "");
      }
      setWorkflowList((current) =>
        current.map((item) => (String(getRequestId(item)) === String(requestId) ? mergeDefined(item, updatedRequest) : item))
      );
      setRequestHistory((current) =>
        current.map((item) => (String(getRequestId(item)) === String(requestId) ? mergeDefined(item, updatedRequest) : item))
      );
      fetchWorkflowSummary();
      fetchWorkflowList();
      fetchRequestHistory(selectedPatientId);
      if (requestId && String(requestId) === String(getRequestId(selectedRequest))) fetchRequestDetails(requestId, updatedRequest);
      setGeneratedFileName("");
      if (showSuccessToast) showToast("success", "Workflow updated", successMessage);
      return true;
    } catch (err) {
      console.error(err);
      showToast("error", "Action failed", "Statement action failed. Please try again. " + err.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const updateStatus = async (endpoint, payload, successMessage) => {
    await updateStatusForRequest(selectedRequest, endpoint, payload, successMessage);
  };

  const handleApprove = () => {
    updateStatus(
      PatientStatementsConstants.ApproveStatement,
      {
        BillingReviewedByUserId: Number(billingUserId) || 0,
        BillingReviewedByName: billingUserName || "",
        BillingReviewNotes: approveNotes || "",
      },
      "Statement approved."
    );
  };

  const handleReject = () => {
    if (!rejectNotes.trim()) {
      showToast("warning", "Reject notes required", "Please enter reject reason.");
      return;
    }
    updateStatus(
      PatientStatementsConstants.RejectStatement,
      {
        BillingReviewedByUserId: Number(billingUserId) || 0,
        BillingReviewedByName: billingUserName || "",
        BillingReviewNotes: rejectNotes,
      },
      "Statement rejected."
    );
  };

  const handleMarkGenerated = () => {
    updateStatus(PatientStatementsConstants.MarkGenerated, { StatementFilePath: generatedFileName || "" }, "Statement marked as generated.");
  };

  const approveRequest = (request, showSuccessToast = true) =>
    updateStatusForRequest(
      request,
      PatientStatementsConstants.ApproveStatement,
      {
        BillingReviewedByUserId: Number(billingUserId) || 0,
        BillingReviewedByName: billingUserName || "",
        BillingReviewNotes: approveNotes || "",
      },
      "Statement approved.",
      showSuccessToast
    );

  const rejectRequest = (request, showSuccessToast = true) =>
    updateStatusForRequest(
      request,
      PatientStatementsConstants.RejectStatement,
      {
        BillingReviewedByUserId: Number(billingUserId) || 0,
        BillingReviewedByName: billingUserName || "",
        BillingReviewNotes: rejectNotes,
      },
      "Statement rejected.",
      showSuccessToast
    );

  const markRequestGenerated = (request, showSuccessToast = true) =>
    updateStatusForRequest(
      request,
      PatientStatementsConstants.MarkGenerated,
      { StatementFilePath: generatedFileName || "" },
      "Statement marked as generated.",
      showSuccessToast
    );

  const handleRowApprove = (request) => approveRequest(request);

  const handleRowReject = (request) => {
    if (!rejectNotes.trim()) {
      showToast("warning", "Reject notes required", "Enter reject notes in Billing Desk > Request Details before rejecting.");
      return;
    }
    rejectRequest(request);
  };

  const handleRowGenerate = (request) => handleGenerateInvoiceForRequest(request);

  const handleRowMarkGenerated = (request) => markRequestGenerated(request);

  const handleBulkWorkflowAction = async (actionType) => {
    if (selectedWorkflowRequests.length === 0) {
      showToast("warning", "No requests selected", "Select one or more statement requests first.");
      return;
    }
    if (actionType === "reject" && !rejectNotes.trim()) {
      showToast("warning", "Reject notes required", "Enter reject notes in Billing Desk > Request Details before rejecting.");
      return;
    }

    setActionLoading(true);
    let processedCount = 0;
    try {
      for (const request of selectedWorkflowRequests) {
        let completed = false;
        if (actionType === "approve") completed = await approveRequest(request, false);
        if (actionType === "reject") completed = await rejectRequest(request, false);
        if (actionType === "generate") completed = await handleGenerateInvoiceForRequest(request, false);
        if (actionType === "markGenerated") completed = await markRequestGenerated(request, false);
        if (completed) processedCount += 1;
      }
      setSelectedStatementIds([]);
      await fetchWorkflowSummary();
      await fetchWorkflowList();
      showToast("success", "Workflow updated", `${processedCount} selected request(s) processed.`);
    } catch {
      // Individual helper already shows the error toast.
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="hv-page hv-page--dense psw-page">
      {toast && (
        <div className={`psw-toast psw-toast-${toast.type}`} role="status" aria-live="polite">
          <div className="psw-toast-icon">{toast.type === "success" ? "OK" : toast.type === "warning" ? "!" : "x"}</div>
          <div className="psw-toast-copy">
            <strong>{toast.title}</strong>
            <span>{toast.message}</span>
          </div>
              <button type="button" className="psw-toast-close" onClick={() => setToast(null)} aria-label="Dismiss notification">
                x
          </button>
        </div>
      )}

      {pdfViewer.isOpen && (
        <div className="psw-pdf-modal" role="dialog" aria-modal="true" aria-label={pdfViewer.title}>
          <div className="psw-pdf-dialog">
            <div className="psw-pdf-header">
              <div>
                <h3>{pdfViewer.title}</h3>
                <span>{pdfViewer.url}</span>
              </div>
              <div className="psw-pdf-actions">
                <a className="hv-btn psw-btn psw-btn-light" href={pdfViewer.url} target="_blank" rel="noreferrer">
                  Open
                </a>
                <button type="button" className="hv-btn psw-btn psw-btn-primary" onClick={closePdfViewer}>
                  Close
                </button>
              </div>
            </div>
            <iframe className="psw-pdf-frame" title={pdfViewer.title} src={pdfViewer.url} />
          </div>
        </div>
      )}

      <div className="psw-role-tabs" role="tablist" aria-label="Patient statement user desk">
        <button type="button" className={bottomDeskTab === "frontDesk" ? "is-active" : ""} onClick={() => setBottomDeskTab("frontDesk")}>
          Front Desk
        </button>
        <button type="button" className={bottomDeskTab === "billingDesk" ? "is-active" : ""} onClick={() => setBottomDeskTab("billingDesk")}>
          Billing Desk
        </button>
      </div>

      <section className="hv-pagehead psw-topbar">
        <div className="psw-title-block">
          <h2>Patient Statement Workflow</h2>
          <span>Invoice / Receipt Module</span>
        </div>

        <div className="hv-toolbar psw-filter-bar">
          <select className="hv-select psw-input psw-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Pending Review">Pending Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Generated">Generated</option>
          </select>
          <label className="psw-field">
            <span>From</span>
            <input className="hv-input psw-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </label>
          <label className="psw-field">
            <span>To</span>
            <input className="hv-input psw-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </label>
          <input
            type="search"
            className="hv-input psw-input psw-search"
            placeholder="Search requests..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={() => { fetchWorkflowSummary(); fetchWorkflowList(); if (getRequestId(selectedRequest)) fetchRequestDetails(getRequestId(selectedRequest)); }}>
            Refresh
          </button>
        </div>
      </section>

      <section className="psw-create-panel psw-create-panel-top-hidden">
        <div className="psw-create-head">
          <div>
            <h3>Create Statement Request</h3>
            <span>Find a patient and generate a workflow request from this screen</span>
          </div>
          {selectedPatient && <strong>{getPatientName(selectedPatient)}</strong>}
        </div>

        <div className="psw-create-grid">
          <label className="psw-patient-finder">
            <span>Patient</span>
            <input
              className="hv-input psw-input"
              type="text"
              value={patientSearchText}
              placeholder="Type patient name..."
              autoComplete="off"
              onChange={(e) => fetchPatientSuggestions(e.target.value)}
            />
            {patientSuggestions.length > 0 && (
              <div className="psw-suggestions">
                {patientSuggestions.map((suggestion) => (
                  <button key={`${suggestion.val}-${suggestion.label}`} type="button" onClick={() => selectPatient(suggestion)}>
                    <strong>{suggestion.label}</strong>
                    {suggestion.fullData?.DOB && <span>DOB: {formatDateTime(suggestion.fullData.DOB).split(",")[0]}</span>}
                  </button>
                ))}
              </div>
            )}
          </label>

          <label className="psw-field">
            <span>Account #</span>
            <input className="hv-input psw-input" type="text" value={requestAccountNumber} onChange={(e) => setRequestAccountNumber(e.target.value)} />
          </label>

          <label className="psw-field">
            <span>Outstanding Balance</span>
            <input className="hv-input psw-input" type="number" min="0" step="0.01" value={requestBalance} onChange={(e) => setRequestBalance(e.target.value)} />
          </label>

          <label className="psw-field">
            <span>DOS</span>
            <input className="hv-input psw-input" type="date" value={requestDos} onChange={(e) => setRequestDos(e.target.value)} />
          </label>

          <label className="psw-field">
            <span>From Date</span>
            <input className="hv-input psw-input" type="date" value={requestStatementFromDate} onChange={(e) => setRequestStatementFromDate(e.target.value)} />
          </label>

          <label className="psw-field">
            <span>To Date</span>
            <input className="hv-input psw-input" type="date" value={requestStatementToDate} onChange={(e) => setRequestStatementToDate(e.target.value)} />
          </label>

          <label className="psw-field psw-create-notes">
            <span>Request Notes</span>
            <input className="hv-input psw-input" type="text" value={requestNotes} onChange={(e) => setRequestNotes(e.target.value)} placeholder="Optional notes" />
          </label>

          <div className="psw-create-actions">
            <button className="hv-btn psw-btn psw-btn-light" type="button" onClick={clearRequestForm} disabled={createLoading}>
              Clear
            </button>
            <button className="hv-btn psw-btn psw-btn-success" type="button" onClick={handleCreateRequest} disabled={createLoading || !selectedPatient}>
              Create Request
            </button>
          </div>
        </div>
      </section>

      <section className="psw-metrics" aria-label="Statement workflow summary">
        {statusTiles.map((tile) => {
          const count = tile.key === "Pending Review" ? summary.pendingReview : tile.key === "Approved" ? summary.approved : tile.key === "Rejected" ? summary.rejected : tile.key === "Generated" ? summary.generated : workflowList.length;
          return (
            <button
              key={tile.key}
              type="button"
              className={`psw-metric ${filterStatus === tile.key ? "is-active" : ""}`}
              onClick={() => setFilterStatus(tile.key)}
            >
              <span>{tile.label}</span>
              <strong style={{ color: tile.color }}>{count}</strong>
            </button>
          );
        })}
      </section>

      {error && (
        <div className="psw-alert" role="alert">
          {error}
        </div>
      )}

      <section className="psw-workspace">
        <div className="hv-panel psw-panel psw-list-panel">
          <div className="hv-panel__head psw-panel-head">
            <div>
              <h3>Statement Requests</h3>
              <span>{workflowList.length} request{workflowList.length === 1 ? "" : "s"}</span>
            </div>
            {loading && <span className="psw-loading">Loading...</span>}
          </div>
          {selectedStatementIds.length > 0 && (
            <div className="psw-bulk-actions">
              <strong>{selectedStatementIds.length} selected</strong>
              <button className="hv-btn psw-btn psw-btn-success" type="button" onClick={() => handleBulkWorkflowAction("approve")} disabled={actionLoading}>
                Approve
              </button>
              <button className="hv-btn psw-btn psw-btn-danger" type="button" onClick={() => handleBulkWorkflowAction("reject")} disabled={actionLoading}>
                Reject
              </button>
              <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={() => handleBulkWorkflowAction("generate")} disabled={billingActionLoading}>
                Generate Statement
              </button>
              <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={() => handleBulkWorkflowAction("markGenerated")} disabled={actionLoading}>
                Mark as Generated
              </button>
            </div>
          )}
          <div className="hv-table-scroll psw-table-wrap">
            <table className="hv-table hv-table--compact hv-table--striped psw-table">
              <thead>
                <tr>
                  <th className="psw-select-th">
                    <input
                      type="checkbox"
                      checked={allVisibleWorkflowSelected}
                      onChange={toggleVisibleStatementSelection}
                      aria-label="Select all visible statement requests"
                    />
                  </th>
                  {workflowColumns.map((column) => (
                    <SortableHeader key={column.key} tableKey="workflow" column={column} />
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleWorkflowList.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="psw-empty">
                      {loading ? "Loading requests..." : "No workflow requests found."}
                    </td>
                  </tr>
                ) : (
                  visibleWorkflowList.map((item) => {
                    const requestId = getRequestId(item);
                    const status = getStatus(item);
                    const isSelected = String(requestId) === String(getRequestId(selectedRequest));
                    const isChecked = selectedStatementIds.includes(String(requestId));
                    const canRowReview = !["Approved", "Rejected", "Generated"].includes(status);
                    const canRowGenerate = status === "Approved";
                    return (
                      <tr key={requestId || `${pick(item, ["PatientName", "Patient", "Name"], "")}-${pick(item, ["AccountNumber", "Account", "AccountNo"], "")}`} className={isSelected ? "is-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleStatementSelection(requestId)}
                            aria-label={`Select statement request ${requestId || ""}`}
                          />
                        </td>
                        <td>{requestId || "-"}</td>
                        <td>{pick(item, ["PatientName", "Patient", "Name"])}</td>
                        <td>{pick(item, ["AccountNumber", "Account", "AccountNo"])}</td>
                        <td>{pick(item, ["OutstandingBalance", "Balance", "Amount"])}</td>
                        <td>{formatDateOnly(pick(item, ["StatementFromDate", "FromDate", "StatementStartDate"], ""))}</td>
                        <td>{formatDateOnly(pick(item, ["StatementToDate", "ToDate", "StatementEndDate"], ""))}</td>
                        <td>{pick(item, ["RequestedByName", "RequestedBy", "CreatedBy", "requestedBy"])}</td>
                        <td>{formatDateTime(pick(item, ["RequestDate", "CreatedOn", "Date"], ""))}</td>
                        <td>
                          <span className="psw-status" style={getStatusBadgeStyle(status)}>{status || "-"}</span>
                        </td>
                        <td className="psw-row-actions">
                          <button className="hv-btn psw-btn psw-btn-light" type="button" onClick={() => fetchRequestDetails(requestId, item, { showInFrontDesk: bottomDeskTab === "frontDesk" })}>
                            View
                          </button>
                          <button className="hv-btn psw-btn psw-btn-success" type="button" onClick={() => handleRowApprove(item)} disabled={actionLoading || !canRowReview}>
                            Approve
                          </button>
                          <button className="hv-btn psw-btn psw-btn-danger" type="button" onClick={() => handleRowReject(item)} disabled={actionLoading || !canRowReview}>
                            Reject
                          </button>
                          <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={() => handleRowGenerate(item)} disabled={billingActionLoading || !canRowGenerate}>
                            Generate
                          </button>
                          <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={() => handleRowMarkGenerated(item)} disabled={actionLoading || !canRowGenerate}>
                            Mark Generated
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="psw-desk-panel">
          {bottomDeskTab === "billingDesk" && billingDeskTab === "requestDetails" ? (
            <div className="psw-request-details-tab">
              <div className="psw-sub-tabs" role="tablist" aria-label="Billing desk tabs">
                <button type="button" className={billingDeskTab === "requestDetails" ? "is-active" : ""} onClick={() => setBillingDeskTab("requestDetails")}>
                  Request Details
                </button>
                <button type="button" className={billingDeskTab === "ledger" ? "is-active" : ""} onClick={() => setBillingDeskTab("ledger")}>
                  Ledger
                </button>
                <button type="button" className={billingDeskTab === "requestHistory" ? "is-active" : ""} onClick={() => setBillingDeskTab("requestHistory")}>
                  Statement History
                </button>
              </div>
              <div className="hv-panel__head psw-panel-head">
                <div>
                  <h3>Request Details</h3>
                  <span>{selectedRequest ? `Request ${getRequestId(selectedFields)}` : "No request selected"}</span>
                </div>
                {selectedRequest && <span className="psw-status" style={getStatusBadgeStyle(selectedStatus)}>{selectedStatus || "-"}</span>}
              </div>

              {!selectedRequest ? (
                <div className="psw-empty-state">Select a request from the list to review workflow actions.</div>
              ) : (
                <>
                  <div className="psw-detail-scroll psw-detail-scroll-inline">
                    <div className="psw-detail-grid psw-detail-grid-wide">
                      <div>
                        <span>Request ID</span>
                        <strong>{getRequestId(selectedFields) || "-"}</strong>
                      </div>
                      <div>
                        <span>Patient Name</span>
                        <strong>{pick(selectedFields, ["PatientName", "Patient", "Name"])}</strong>
                      </div>
                      <div>
                        <span>Account #</span>
                        <strong>{pick(selectedFields, ["AccountNumber", "Account", "AccountNo"])}</strong>
                      </div>
                      <div>
                        <span>Outstanding Balance</span>
                        <strong>{formatMoney(displayedOutstandingBalance)}</strong>
                      </div>
                      <div>
                        <span>Requested By</span>
                        <strong>{pick(selectedFields, ["RequestedByName", "RequestedBy", "CreatedBy"])}</strong>
                      </div>
                      <div>
                        <span>Request Date</span>
                        <strong>{formatDateTime(pick(selectedFields, ["RequestDate", "CreatedOn", "Date"], ""))}</strong>
                      </div>
                      <div>
                        <span>Stored From Date</span>
                        <strong>{formatDateOnly(selectedStatementFromDate)}</strong>
                      </div>
                      <div>
                        <span>Stored To Date</span>
                        <strong>{formatDateOnly(selectedStatementToDate)}</strong>
                      </div>
                      <div className="psw-detail-wide">
                        <span>Notes</span>
                        <p>{pick(selectedFields, ["Notes", "RequestNotes", "Comments"])}</p>
                      </div>
                      <div>
                        <span>Reviewed By</span>
                        <strong>{selectedReviewedBy || "-"}</strong>
                      </div>
                      <div className="psw-detail-wide">
                        <span>Approval / Reject Notes</span>
                        <p>{selectedReviewNotes || "-"}</p>
                      </div>
                    </div>

                    <div className="psw-action-fields psw-action-fields-inline">
                      <div className="psw-request-date-filter">
                        <label>
                          <span>From:</span>
                          <input className="hv-date psw-ledger-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={completePatientStatement} />
                        </label>
                        <label>
                          <span>To:</span>
                          <input className="hv-date psw-ledger-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={completePatientStatement} />
                        </label>
                        <label className="psw-inline-check">
                          <input
                            type="checkbox"
                            checked={completePatientStatement}
                            onChange={(e) => setCompletePatientStatement(e.target.checked)}
                          />
                          <span style={{ color: "#fff" }}>Complete Patient Statement</span>
                        </label>
                      </div>
                      <label>
                        <span>Approve Notes (Optional)</span>
                        <textarea rows="2" value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} disabled={!canReview} />
                      </label>
                      <label>
                        <span>Reject Notes (Required)</span>
                        <textarea rows="2" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} disabled={!canReview} />
                      </label>
                      <label style={{ display: "none" }}>
                        <span>Statement PDF</span>
                        <input
                          type="file"
                          accept="application/pdf"
                          disabled={!canMarkGenerated}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            setGeneratedFileName(file?.name || "");
                          }}
                        />
                      </label>
                      {generatedFileName && <div className="psw-file-name">Selected file: {generatedFileName}</div>}
                    </div>
                  </div>

                  <div className="psw-action-bar">
                    <button className="hv-btn psw-btn psw-btn-success" type="button" onClick={handleApprove} disabled={actionLoading || !canReview}>
                      Approve
                    </button>
                    <button className="hv-btn psw-btn psw-btn-danger" type="button" onClick={handleReject} disabled={actionLoading || !canReview}>
                      Reject
                    </button>
                    <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={handleGenerateInvoice} disabled={billingActionLoading || !canGenerateInvoice}>
                      Generate Statement
                    </button>
                    <button className="hv-btn psw-btn psw-btn-primary" type="button" onClick={handleMarkGenerated} disabled={actionLoading || !canMarkGenerated}>
                      Mark as Generated
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : bottomDeskTab === "billingDesk" && billingDeskTab === "requestHistory" ? (
            <div className="psw-request-history-tab">
              <div className="psw-sub-tabs" role="tablist" aria-label="Billing desk tabs">
                <button type="button" className={billingDeskTab === "requestDetails" ? "is-active" : ""} onClick={() => setBillingDeskTab("requestDetails")}>
                  Request Details
                </button>
                <button type="button" className={billingDeskTab === "ledger" ? "is-active" : ""} onClick={() => setBillingDeskTab("ledger")}>
                  Ledger
                </button>
                <button type="button" className={billingDeskTab === "requestHistory" ? "is-active" : ""} onClick={() => setBillingDeskTab("requestHistory")}>
                  Statement History
                </button>
              </div>
              <div className="hv-panel__head psw-panel-head">
                <div>
                  <h3>Statement Request History</h3>
                  <span>{selectedPatientName || "Selected patient"} | Previous statement workflow requests</span>
                </div>
                <button className="hv-btn psw-btn psw-btn-light" type="button" onClick={() => fetchRequestHistory()} disabled={historyLoading || !selectedPatientId}>
                  Refresh
                </button>
              </div>

              {historyError ? (
                <div className="psw-ledger-warning">{historyError}</div>
              ) : null}

              <div className="hv-table-scroll psw-ledger-table-wrap psw-request-history-table">
                <table className="hv-table hv-table--compact hv-table--striped psw-ledger-table">
                  <thead>
                    <tr>
                      {requestHistoryColumns.map((column) => (
                        <SortableHeader key={column.key} tableKey="requestHistory" column={column} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading ? (
                      <tr>
                        <td colSpan="13" className="psw-empty">Loading statement request history...</td>
                      </tr>
                    ) : visibleRequestHistory.length === 0 ? (
                      <tr>
                        <td colSpan="13" className="psw-empty">No previous statement requests found for this patient.</td>
                      </tr>
                    ) : visibleRequestHistory.map((request, index) => (
                      <tr key={`history-${getRequestId(request) || index}`} className={String(getRequestId(request)) === String(getRequestId(selectedRequest)) ? "is-selected" : ""}>
                        <td>{getRequestId(request) || "-"}</td>
                        <td>{pick(request, ["PatientName", "Patient", "Name"])}</td>
                        <td>{pick(request, ["AccountNumber", "Account", "AccountNo"])}</td>
                        <td>{formatMoney(pick(request, ["OutstandingBalance", "Balance", "Amount"], 0))}</td>
                        <td>{formatDateOnly(pick(request, ["DOS", "ServiceDate"], ""))}</td>
                        <td>{formatDateOnly(pick(request, ["StatementFromDate", "FromDate", "StatementStartDate"], ""))}</td>
                        <td>{formatDateOnly(pick(request, ["StatementToDate", "ToDate", "StatementEndDate"], ""))}</td>
                        <td>{pick(request, ["RequestedByName", "RequestedBy", "CreatedBy"])}</td>
                        <td>{formatDateTime(pick(request, ["RequestDate", "CreatedDate", "CreatedOn", "Date"], ""))}</td>
                        <td><span className="psw-status" style={getStatusBadgeStyle(getStatus(request))}>{getStatus(request) || "-"}</span></td>
                        <td>{pick(request, ["Notes", "RequestNotes", "Comments"])}</td>
                        <td>{pick(request, ["BillingReviewNotes", "ReviewNotes", "ApprovalNotes", "ApproveNotes", "RejectNotes"])}</td>
                        <td>{pick(request, ["BillingReviewedByName", "ReviewedByName", "ApprovedBy", "RejectedBy"])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : bottomDeskTab === "frontDesk" ? (
            <div className="psw-frontdesk">
              <div className="psw-sub-tabs" role="tablist" aria-label="Front desk tabs">
                <button type="button" className={frontDeskTab === "statementBilling" ? "is-active" : ""} onClick={() => setFrontDeskTab("statementBilling")}>
                  Statement Request / Billing
                </button>
                <button type="button" className={frontDeskTab === "ledger" ? "is-active" : ""} onClick={() => setFrontDeskTab("ledger")}>
                  Ledger
                </button>
                <button type="button" className={frontDeskTab === "requestHistory" ? "is-active" : ""} onClick={() => setFrontDeskTab("requestHistory")}>
                  Statement History
                </button>
              </div>

              {frontDeskTab === "statementBilling" ? (
                <>
                <div className="psw-create-tab-panel">
                  <div className="psw-create-head">
                    <div>
                      <h3>{editingStatementRequestId ? "Statement Request Details" : "Create Statement Request"}</h3>
                      <span>
                        {editingStatementRequestId
                          ? `Edit mode | Request ${editingStatementRequestId}`
                          : "New mode | Find a patient and generate a workflow request from this screen"}
                      </span>
                    </div>
                    {selectedPatient && <strong>{getPatientName(selectedPatient)}</strong>}
                  </div>

                  <div className="psw-create-grid">
                    <label className="psw-patient-finder psw-create-patient">
                      <span>Patient</span>
                      <input
                        className="hv-input psw-input"
                        type="text"
                        value={patientSearchText}
                        placeholder="Type patient name..."
                        autoComplete="off"
                        onChange={(e) => fetchPatientSuggestions(e.target.value)}
                      />
                      {patientSuggestions.length > 0 && (
                        <div className="psw-suggestions">
                          {patientSuggestions.map((suggestion) => (
                            <button key={`${suggestion.val}-${suggestion.label}`} type="button" onClick={() => selectPatient(suggestion)}>
                              <strong>{suggestion.label}</strong>
                              {suggestion.fullData?.DOB && <span>DOB: {formatDateTime(suggestion.fullData.DOB).split(",")[0]}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </label>

                    <label className="psw-field psw-create-account">
                      <span>Account #</span>
                      <input className="hv-input psw-input" type="text" value={requestAccountNumber} onChange={(e) => setRequestAccountNumber(e.target.value)} />
                    </label>

                    <label className="psw-field psw-create-balance">
                      <span>Outstanding Balance</span>
                      <input className="hv-input psw-input" type="number" min="0" step="0.01" value={requestBalance} onChange={(e) => setRequestBalance(e.target.value)} />
                    </label>

                    <label className="psw-field psw-create-dos">
                      <span>DOS</span>
                      <input className="hv-input psw-input" type="date" value={requestDos} onChange={(e) => setRequestDos(e.target.value)} />
                    </label>

                    <label className="psw-field psw-create-from">
                      <span>From Date</span>
                      <input className="hv-input psw-input" type="date" value={requestStatementFromDate} onChange={(e) => setRequestStatementFromDate(e.target.value)} />
                    </label>

                    <label className="psw-field psw-create-to">
                      <span>To Date</span>
                      <input className="hv-input psw-input" type="date" value={requestStatementToDate} onChange={(e) => setRequestStatementToDate(e.target.value)} />
                    </label>

                    <label className="psw-field psw-create-notes">
                      <span>Request Notes</span>
                      <input className="hv-input psw-input" type="text" value={requestNotes} onChange={(e) => setRequestNotes(e.target.value)} placeholder="Optional notes" />
                    </label>

                    <div className="psw-create-actions">
                      <button className="hv-btn psw-btn psw-btn-light" type="button" onClick={clearRequestForm} disabled={createLoading}>
                        Clear
                      </button>
                      <button className="hv-btn psw-btn psw-btn-success" type="button" onClick={handleCreateRequest} disabled={createLoading || !selectedPatient}>
                        Create Request
                      </button>
                    </div>
                  </div>
                </div>
                <div className="psw-frontdesk-billing">
                  <div className="psw-classic-title">Billing Copay Invoice</div>
                  <div className="psw-classic-form">
                    <label>
                      <span>DOS</span>
                      <input type="date" value={billingDos} onChange={(e) => setBillingDos(e.target.value)} />
                    </label>
                    <label>
                      <span>POS</span>
                      <select value={billingPos} onChange={(e) => setBillingPos(e.target.value)}>
                        <option value="0">--Select--</option>
                        <option value="11">Office</option>
                        <option value="12">Home</option>
                        <option value="22">Outpatient</option>
                      </select>
                    </label>
                    <div className="psw-copay-actions">
                      <button
                        type="button"
                        className={`hv-btn psw-btn ${useCopayPayment ? "psw-btn-success" : "psw-btn-light"}`}
                        onClick={() => setUseCopayPayment((current) => !current)}
                      >
                        {useCopayPayment ? "Copay Applied" : "Apply CoPay"}
                      </button>
                      <strong>Patient CoPay {formatMoney(frontDeskCopay)}</strong>
                      <button type="button" className="hv-btn psw-btn psw-btn-light" onClick={() => fetchFrontDeskDashboard()} disabled={frontDeskLoading}>
                        Refresh
                      </button>
                    </div>
                    <div className="psw-payment-splits">
                      <div className="psw-payment-splits-head">
                        <span>Payments</span>
                        <strong>Total {formatMoney(billingPaymentTotal)}</strong>
                      </div>
                      {paymentSplits.map((split, index) => {
                        const referenceLabel = getPaymentReferenceLabel(split.mode);
                        return (
                          <div className="psw-payment-split-row" key={split.id}>
                            <label>
                              <span>Amount {index + 1}</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={split.amount}
                                onChange={(e) => updatePaymentSplit(split.id, { amount: e.target.value })}
                              />
                            </label>
                            <label>
                              <span>Mode</span>
                              <select value={split.mode} onChange={(e) => updatePaymentSplit(split.id, { mode: e.target.value })}>
                                <option value="0">Cash</option>
                                <option value="1">Cheque</option>
                                <option value="2">CC</option>
                                <option value="5">Debit Card</option>
                              </select>
                            </label>
                            {referenceLabel && (
                              <label>
                                <span>{referenceLabel}</span>
                                <input
                                  type="text"
                                  value={split.paymentReferenceNumber}
                                  onChange={(e) => updatePaymentSplit(split.id, { paymentReferenceNumber: e.target.value })}
                                />
                              </label>
                            )}
                            {split.mode === "1" && (
                              <label>
                                <span>Cheque Date</span>
                                <input type="date" value={split.chequeDate} onChange={(e) => updatePaymentSplit(split.id, { chequeDate: e.target.value })} />
                              </label>
                            )}
                            <button type="button" className="psw-row-delete" onClick={() => removePaymentSplit(split.id)} aria-label="Remove payment split">
                              x
                            </button>
                          </div>
                        );
                      })}
                      <button type="button" className="hv-btn psw-btn psw-btn-light psw-add-payment" onClick={addPaymentSplit}>
                        Add Payment
                      </button>
                    </div>
                  </div>

                  <div className="psw-balance-strip">
                    <span>Total balance due :</span>
                    <strong>{formatMoney(billingBalance)}</strong>
                    <button type="button" className="psw-classic-save" onClick={handleGenerateReceipt} disabled={billingActionLoading}>
                      Save
                    </button>
                    <button type="button" className="psw-classic-receipt" onClick={handleGenerateReceipt} disabled={billingActionLoading}>
                      Receipt
                    </button>
                  </div>

                  <div className="hv-table-scroll psw-ledger-table-wrap">
                    <table className="hv-table hv-table--compact hv-table--striped psw-ledger-table psw-frontdesk-table">
                      <thead>
                        <tr>
                          {frontDeskBillingColumns.map((column) => (
                            <SortableHeader key={column.key} tableKey="frontDeskBilling" column={column} />
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {frontDeskLoading ? (
                          <tr>
                            <td colSpan="9" className="psw-empty">Loading billing ledger...</td>
                          </tr>
                        ) : visibleFrontDeskBillingRows.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="psw-empty">No recent paid ledger records found.</td>
                          </tr>
                        ) : visibleFrontDeskBillingRows.map((row, index) => (
                          <tr key={`fd-billing-${index}`}>
                            <td>{getRowValue(row, ["PracticeInvoiceNumber", "practiceinvoiceNumber", "InvoiceNumber", "invoiceNumber"])}</td>
                            <td>{formatDateOnly(getRowValue(row, ["FromServiceDate", "from_service_date", "FromDate"], ""))}</td>
                            <td>{formatDateOnly(getRowValue(row, ["ToServiceDate", "to_service_date", "ToDate"], ""))}</td>
                            <td>{getRowValue(row, ["Status", "status"])}</td>
                            <td>{getRowValue(row, ["AmountPaid", "amountPaid", "paidamountString"])}</td>
                            <td>{getRowValue(row, ["PaymentMode", "paymentMode", "paymentmodestring"])}</td>
                            <td>{getRowValue(row, ["PaymentReferenceNumber", "paymentReferenceNumber", "PaymentRefNo"])}</td>
                            <td>
                              {getRowValue(row, ["ReceiptUrl", "PDFURL"], "") ? (
                                <button type="button" className="psw-link-action" onClick={() => openDocumentUrl(getRowValue(row, ["ReceiptUrl", "PDFURL"], ""), "Patient Receipt")}>
                                  View
                                </button>
                              ) : null}
                              <button type="button" className="psw-link-action" onClick={() => handleRegenerateLedgerDocument(row, "Receipt")} disabled={billingActionLoading}>
                                Regenerate
                              </button>
                            </td>
                            <td>{getRowValue(row, ["StatementBalance", "LedgerBalance", "Balance", "balancedue"])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </div>
                </>
              ) : frontDeskTab === "requestHistory" ? (
                <div className="psw-request-history-tab">
                  <div className="hv-panel__head psw-panel-head">
                    <div>
                      <h3>Statement Request History</h3>
                      <span>{selectedPatientName || "Selected patient"} | Previous statement workflow requests</span>
                    </div>
                    <button className="hv-btn psw-btn psw-btn-light" type="button" onClick={() => fetchRequestHistory()} disabled={historyLoading || !selectedPatientId}>
                      Refresh
                    </button>
                  </div>

                  {historyError ? (
                    <div className="psw-ledger-warning">{historyError}</div>
                  ) : null}

                  <div className="hv-table-scroll psw-ledger-table-wrap psw-request-history-table">
                    <table className="hv-table hv-table--compact hv-table--striped psw-ledger-table">
                      <thead>
                        <tr>
                          {requestHistoryColumns.map((column) => (
                            <SortableHeader key={column.key} tableKey="requestHistory" column={column} />
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {historyLoading ? (
                          <tr>
                            <td colSpan="13" className="psw-empty">Loading statement request history...</td>
                          </tr>
                        ) : visibleRequestHistory.length === 0 ? (
                          <tr>
                            <td colSpan="13" className="psw-empty">No previous statement requests found for this patient.</td>
                          </tr>
                        ) : visibleRequestHistory.map((request, index) => (
                          <tr key={`fd-history-${getRequestId(request) || index}`} className={String(getRequestId(request)) === String(getRequestId(selectedRequest)) ? "is-selected" : ""}>
                            <td>{getRequestId(request) || "-"}</td>
                            <td>{pick(request, ["PatientName", "Patient", "Name"])}</td>
                            <td>{pick(request, ["AccountNumber", "Account", "AccountNo"])}</td>
                            <td>{formatMoney(pick(request, ["OutstandingBalance", "Balance", "Amount"], 0))}</td>
                            <td>{formatDateOnly(pick(request, ["DOS", "ServiceDate"], ""))}</td>
                            <td>{formatDateOnly(pick(request, ["StatementFromDate", "FromDate", "StatementStartDate"], ""))}</td>
                            <td>{formatDateOnly(pick(request, ["StatementToDate", "ToDate", "StatementEndDate"], ""))}</td>
                            <td>{pick(request, ["RequestedByName", "RequestedBy", "CreatedBy"])}</td>
                            <td>{formatDateTime(pick(request, ["RequestDate", "CreatedDate", "CreatedOn", "Date"], ""))}</td>
                            <td><span className="psw-status" style={getStatusBadgeStyle(getStatus(request))}>{getStatus(request) || "-"}</span></td>
                            <td>{pick(request, ["Notes", "RequestNotes", "Comments"])}</td>
                            <td>{pick(request, ["BillingReviewNotes", "ReviewNotes", "ApprovalNotes", "ApproveNotes", "RejectNotes"])}</td>
                            <td>{pick(request, ["BillingReviewedByName", "ReviewedByName", "ApprovedBy", "RejectedBy"])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="psw-frontdesk-ledger">
                  <div className="psw-ledger-head">
                    <div>
                      <h3>Front Desk Ledger</h3>
                      <span>{selectedPatientName} | {formatDateOnly(fromDate)} - {formatDateOnly(toDate)}</span>
                    </div>
                    <div className="psw-ledger-tools">
                      <label>
                        <span>From:</span>
                        <input className="hv-date psw-ledger-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                      </label>
                      <label>
                        <span>To:</span>
                        <input className="hv-date psw-ledger-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                      </label>
                      <button className="psw-ledger-search" type="button" onClick={() => fetchFrontDeskDashboard()} disabled={frontDeskLoading || !selectedPatientId}>
                        Search
                      </button>
                    </div>
                  </div>

                  {frontDeskError ? (
                    <div className="psw-ledger-empty">{frontDeskError}</div>
                  ) : frontDeskLoading ? (
                    <div className="psw-ledger-empty">Loading front desk ledger...</div>
                  ) : (
                    <div className="psw-ledger-section">
                      <div className="hv-table-scroll psw-ledger-table-wrap">
                        <table className="hv-table hv-table--compact hv-table--striped psw-ledger-table psw-frontdesk-table">
                          <thead>
                            <tr>
                              {frontDeskLedgerColumns.map((column) => (
                                <SortableHeader key={column.key} tableKey="frontDeskLedger" column={column} />
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {visibleFrontDeskRows.length === 0 ? (
                              <tr>
                                <td colSpan="10" className="psw-empty">No ledger records found.</td>
                              </tr>
                            ) : visibleFrontDeskRows.map((row, index) => (
                              <tr key={`fd-ledger-${index}`}>
                                <td>{getRowValue(row, ["PracticeInvoiceNumber", "practiceinvoiceNumber", "InvoiceNumber", "invoiceNumber"])}</td>
                                <td>{formatDateOnly(getRowValue(row, ["FromServiceDate", "from_service_date", "FromDate"], ""))}</td>
                                <td>{formatDateOnly(getRowValue(row, ["ToServiceDate", "to_service_date", "ToDate"], ""))}</td>
                                <td>{getRowValue(row, ["Status", "status"])}</td>
                                <td>{getRowValue(row, ["AmountPaid", "amountPaid", "paidamountString"])}</td>
                                <td>{getRowValue(row, ["PaymentMode", "paymentMode", "paymentmodestring"])}</td>
                                <td>{getRowValue(row, ["PaymentReferenceNumber", "paymentReferenceNumber", "PaymentRefNo"])}</td>
                                <td>
                                  {getRowValue(row, ["ReceiptUrl", "PDFURL"], "") ? (
                                    <button type="button" className="psw-link-action" onClick={() => openDocumentUrl(getRowValue(row, ["ReceiptUrl", "PDFURL"], ""), "Patient Receipt")}>
                                      View
                                    </button>
                                  ) : null}
                                  <button type="button" className="psw-link-action" onClick={() => handleRegenerateLedgerDocument(row, "Receipt")} disabled={billingActionLoading}>
                                    Regenerate
                                  </button>
                                </td>
                                <td>{getRowValue(row, ["StatementBalance", "LedgerBalance", "Balance", "balancedue"])}</td>
                                <td>
                                  {getRowValue(row, ["StatementUrl", "PDFStatURL"], "") ? (
                                    <button type="button" className="psw-link-action" onClick={() => openDocumentUrl(getRowValue(row, ["StatementUrl", "PDFStatURL"], ""), "Patient Statement")}>
                                      View
                                    </button>
                                  ) : null}
                                  <button type="button" className="psw-link-action" onClick={() => handleRegenerateLedgerDocument(row, "Statement")} disabled={billingActionLoading}>
                                    Regenerate
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {visibleFrontDeskRows.length > 0 && (
                            <tfoot>
                              <tr>
                                <td className="psw-total-label">Total</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td>{formatMoney(sumRowsByKeys(visibleFrontDeskRows, ["AmountPaid", "amountPaid", "paidamountString"]))}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td>{formatMoney(sumRowsByKeys(visibleFrontDeskRows, ["StatementBalance", "LedgerBalance", "Balance", "balancedue"]))}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <section className="psw-ledger-panel psw-billing-desk-panel">
              <div className="psw-sub-tabs" role="tablist" aria-label="Billing desk tabs">
                <button type="button" className={billingDeskTab === "requestDetails" ? "is-active" : ""} onClick={() => setBillingDeskTab("requestDetails")}>
                  Request Details
                </button>
                <button type="button" className={billingDeskTab === "ledger" ? "is-active" : ""} onClick={() => setBillingDeskTab("ledger")}>
                  Ledger
                </button>
                <button type="button" className={billingDeskTab === "requestHistory" ? "is-active" : ""} onClick={() => setBillingDeskTab("requestHistory")}>
                  Statement History
                </button>
              </div>
              <div className="psw-ledger-head">
                <div>
                  <h3>Billing Desk</h3>
                  <span>
                    {selectedPatientName} | {formatDateOnly(fromDate)} - {formatDateOnly(toDate)}
                  </span>
                </div>
                <div className="psw-ledger-tools">
                  <label>
                    <span>From:</span>
                    <input className="hv-date psw-ledger-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </label>
                  <label>
                    <span>To:</span>
                    <input className="hv-date psw-ledger-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </label>
                  <button className="psw-ledger-search" type="button" onClick={handleLedgerSearch} disabled={ledgerLoading || !selectedPatientId} title="Search ledger">
                    Search
                  </button>
                </div>
              </div>

              {ledgerLoading ? (
                <div className="psw-ledger-empty">Loading ledger information...</div>
              ) : ledgerRows.length === 0 && claimRows.length === 0 ? (
                <>
                  {ledgerError && <div className="psw-ledger-warning">Unable to load all ledger information. {ledgerError}</div>}
                  <div className="psw-ledger-empty">No ledger records found for this patient and date range.</div>
                </>
              ) : (
                <>
                  {ledgerError && <div className="psw-ledger-warning">Unable to load all ledger information. {ledgerError}</div>}
                  <div className="psw-ledger-section">
                    <div className="psw-ledger-title">Claims</div>
                    <div className="hv-table-scroll psw-ledger-table-wrap">
                      <table className="hv-table hv-table--compact hv-table--striped psw-ledger-table">
                        <thead>
                          <tr>
                            {billingDeskClaimColumns.map((column) => (
                              <SortableHeader key={column.label} tableKey="billingClaims" column={{ ...column, key: column.label }} />
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {visibleClaimRows.length === 0 ? (
                            <tr>
                              <td colSpan={billingDeskClaimColumns.length} className="psw-empty">
                                No claim records found for this patient and date range.
                              </td>
                            </tr>
                          ) : visibleClaimRows.map((item, index) => {
                            const balance = pick(item, ["claimbln", "ClaimBalance", "claimBalance", "Balance", "balance", "ClaimBal", "PatientBalance", "balanceDue"], pick(selectedFields, ["OutstandingBalance", "Balance", "Amount"]));

                            return (
                              <tr key={`${pick(item, ["ClaimID", "ClaimId", "claimID", "claimId", "claimid", "ClaimNumber", "claimNumber"], index)}-${index}`} className={parseAmount(balance) > 0 ? "has-balance" : ""}>
                                {billingDeskClaimColumns.map((column) => (
                                  <td key={column.label} className={column.className || ""}>
                                    {getClaimColumnValue(item, column, index, selectedFields, selectedPatientName)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                        {visibleClaimRows.length > 0 && (
                          <tfoot>
                            <tr>
                              {billingDeskClaimColumns.map((column) => (
                                <td
                                  key={`claim-total-${column.label}`}
                                  className={column.label === "Location" ? "psw-total-label" : ""}
                                >
                                  {getBillingClaimTotalValue(visibleClaimRows, column)}
                                </td>
                              ))}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  <div className="psw-ledger-section">
                    <div className="psw-ledger-title">Transactions</div>
                    <div className="hv-table-scroll psw-ledger-table-wrap">
                      <table className="hv-table hv-table--compact hv-table--striped psw-ledger-table psw-ledger-transaction-table">
                        <thead>
                          <tr>
                            {transactionColumns.map((column) => (
                              <SortableHeader key={column.key} tableKey="billingTransactions" column={column} />
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {visibleLedgerRows.length === 0 ? (
                            <tr>
                              <td colSpan="11" className="psw-empty">
                                No transaction records found for this patient and date range.
                              </td>
                            </tr>
                          ) : visibleLedgerRows.map((item, index) => (
                              <tr key={`txn-${pick(item, ["ClaimID", "ClaimId"], index)}-${index}`}>
                                <td>{index + 1}</td>
                                <td>{formatDateOnly(pick(item, ["DOS", "ClaimDOS"], ""))}</td>
                                <td>{formatDateOnly(pick(item, ["PostingDate", "DOT"], ""))}</td>
                                <td>{pick(item, ["ClaimID", "ClaimId"])}</td>
                                <td>{pick(item, ["CPT", "CPTOld", "CPTCode"])}</td>
                                <td>{pick(item, ["TransactionCode"])}</td>
                                <td>{pick(item, ["TransactionCodeDescription", "TransactionDescription", "Transaction"])}</td>
                                <td>{pick(item, ["TransactionAmt", "TotalAmt", "Amount"])}</td>
                                <td>{pick(item, ["InsuranceAmtmoney", "InsuranceAmt"])}</td>
                                <td>{pick(item, ["PatientAmtmoney", "PatientAmt"])}</td>
                                <td>{formatDateOnly(pick(item, ["PostingDate", "DOP"], ""))}</td>
                              </tr>
                            ))} 
                        </tbody>
                        {visibleLedgerRows.length > 0 && (
                          <tfoot>
                            <tr>
                              <td></td>
                              <td className="psw-total-label">Total</td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>{formatMoney(sumRowsByKeys(visibleLedgerRows, ["TransactionAmt", "TotalAmt", "Amount"]))}</td>
                              <td>{formatMoney(sumRowsByKeys(visibleLedgerRows, ["InsuranceAmtmoney", "InsuranceAmt"]))}</td>
                              <td>{formatMoney(sumRowsByKeys(visibleLedgerRows, ["PatientAmtmoney", "PatientAmt"]))}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}
      </section>
    </div>
  );
};

export default PatientStatementsWorkflow;
